import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useStore } from '../context/StoreContext';
import { formatCurrency, generateOrderId } from '../services/dataService';
import { Button } from '../components/Button';
import ProtectedAreaGate from '../components/ProtectedAreaGate';
import { Archive, TrendingUp, ChevronDown, Utensils, Settings, X, Save, FileText, CheckCircle, ArrowDown, ArrowUp, Coins, CalendarDays, ChevronLeft, ChevronRight, Clock, HelpCircle, WalletCards, ReceiptText, Plus, BarChart3, Landmark } from 'lucide-react';
import { Transaction, OrderItem, CashMovement, CashierShift, Payment, FinancialEntry } from '../types';
import { api } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { SpotlightTour, SpotlightStep } from '../components/SpotlightTour';
import { advanceFirstAccessTour, getActiveTourArea, stopFirstAccessTour } from '../utils/firstAccessTour';

const COUNTER_TABLE_ID = 0;
type ReportArea = 'DAILY' | 'FINANCIAL' | 'HISTORY';
type ReportTourStep = SpotlightStep & { area: ReportArea; modal?: 'settings' | 'expense' };

export const reportTourSteps: ReportTourStep[] = [
  { area: 'DAILY', target: 'reports-header', title: 'Seu centro de resultados', description: 'Aqui você acompanha vendas, fluxo de caixa e histórico. Os valores são reais da empresa; os exemplos mostrados neste guia não serão salvos.' },
  { area: 'DAILY', target: 'reports-nav', title: 'Três áreas do relatório', description: 'Hoje reúne a operação atual; Financeiro mostra receitas e gastos; Histórico organiza dias, vendas e fechamentos de caixa.' },
  { area: 'DAILY', target: 'reports-daily-summary', title: 'Resumo de hoje', description: 'Faturamento considera contas fechadas desde o início do dia ou turno. Vendas do período, ticket médio e mesas ainda abertas ajudam a interpretar o movimento.' },
  { area: 'DAILY', target: 'reports-daily-payments', title: 'Como os clientes pagaram', description: 'Veja quanto entrou em dinheiro, Pix, débito e crédito no dia. Mesas ainda abertas não entram nesses totais.' },
  { area: 'DAILY', target: 'reports-top-products', title: 'Produtos mais vendidos', description: 'O ranking considera quantidade e faturamento dos itens vendidos hoje. Se não houver vendas, o guia mostra apenas um exemplo visual.' },
  { area: 'DAILY', target: 'reports-settings-button', title: 'Configure o turno', description: 'Defina se o dia operacional acompanha o calendário ou um horário de abertura e fechamento próprio, útil quando o bar fecha após meia-noite.' },
  { area: 'DAILY', modal: 'settings', target: 'reports-settings-toggle', title: 'Ative o turno se precisar', description: 'Com o turno ativo, vendas após a meia-noite podem continuar no dia operacional da abertura. O guia não altera sua configuração.' },
  { area: 'DAILY', modal: 'settings', target: 'reports-settings-hours', title: 'Horários de abertura e fechamento', description: 'Escolha os horários padrão. Eles determinam a separação das vendas por dia operacional.' },
  { area: 'DAILY', modal: 'settings', target: 'reports-settings-save', title: 'Confira antes de salvar', description: 'A prévia mostra o intervalo escolhido. Salve somente quando quiser aplicar à empresa; durante o guia este botão fica desativado.' },
  { area: 'DAILY', target: 'reports-shift', title: 'Gestão do caixa atual', description: 'Esta área aparece quando a gestão de turnos está ativa. O exemplo do guia mostra as ações mesmo que sua empresa ainda não use turnos.' },
  { area: 'DAILY', target: 'reports-deposit', title: 'Suprimento de caixa', description: 'Registre dinheiro colocado na gaveta, como troco adicional. Exige valor, motivo e autorização; não é uma venda.' },
  { area: 'DAILY', target: 'reports-withdrawal', title: 'Sangria', description: 'Registre a retirada de dinheiro da gaveta com valor, motivo e autorização. Sangria reduz o saldo físico esperado, mas não é gasto operacional.' },
  { area: 'DAILY', target: 'reports-close', title: 'Fechar o caixa', description: 'O fechamento exige senha autorizada, imprime o resumo e registra o saldo esperado. No guia nenhuma operação será fechada.' },
  { area: 'DAILY', target: 'reports-open-tables', title: 'Atenção às mesas abertas', description: 'Se houver mesas abertas no fechamento, elas ficam pendentes no histórico para pagamento posterior. Confira antes de confirmar.' },
  { area: 'FINANCIAL', target: 'reports-financial-summary', title: 'Resultado e margem', description: 'Faturamento menos gastos lançados forma o resultado operacional. A margem compara esse resultado com o faturamento. Sangria não conta como despesa.' },
  { area: 'FINANCIAL', target: 'reports-expense-button', title: 'Lance um gasto real', description: 'Compras, aluguel, contas e outras despesas entram no resultado e no fluxo financeiro. O botão abre um formulário de lançamento.' },
  { area: 'FINANCIAL', modal: 'expense', target: 'reports-expense-basics', title: 'Categoria e data do gasto', description: 'Classifique o gasto e escolha a data em que ele ocorreu. Isso organiza o resultado e o fluxo de caixa.' },
  { area: 'FINANCIAL', modal: 'expense', target: 'reports-expense-details', title: 'Descrição e valor', description: 'Informe o que foi pago e o valor real. A observação é opcional e ajuda a guardar fornecedor, nota ou outro detalhe.' },
  { area: 'FINANCIAL', modal: 'expense', target: 'reports-expense-save', title: 'Confirme o lançamento', description: 'Ao salvar, o gasto aparece nos recentes, por categoria e no gráfico. Neste guia a gravação fica bloqueada.' },
  { area: 'FINANCIAL', target: 'reports-trend-range', title: 'Escolha o período do gráfico', description: 'Alterne entre semana, 14 dias e mês para analisar a evolução das receitas e despesas.' },
  { area: 'FINANCIAL', target: 'reports-trend-chart', title: 'Leia a evolução financeira', description: 'A linha azul representa entradas e a amarela, gastos. Passe o mouse ou clique num ponto para ver os valores e o resultado daquele dia.' },
  { area: 'FINANCIAL', target: 'reports-payment-mix', title: 'Composição das vendas', description: 'Compare o total e a participação de cada forma de pagamento no faturamento carregado.' },
  { area: 'FINANCIAL', target: 'reports-recent-expenses', title: 'Gastos recentes', description: 'Os últimos lançamentos ficam listados aqui. Em uma conta nova, o espaço permanece vazio até o primeiro gasto real.' },
  { area: 'FINANCIAL', target: 'reports-expense-categories', title: 'Onde os gastos pesam', description: 'Veja as categorias de despesa ordenadas por valor. Isso ajuda a identificar quais custos mais reduzem o resultado.' },
  { area: 'HISTORY', target: 'reports-weekly', title: 'Ritmo dos últimos sete dias', description: 'As barras mostram o faturamento por dia; total do período e melhor dia resumem a semana.' },
  { area: 'HISTORY', target: 'reports-calendar', title: 'Navegue pelo calendário', description: 'Use o mês atual ou o anterior. Os marcadores mostram dias com vendas, caixas fechados e mesas pendentes.', detail: 'Períodos mais antigos ficam no relatório mensal enviado por e-mail e podem ser removidos dos servidores.' },
  { area: 'HISTORY', target: 'reports-calendar-day', title: 'Selecione um dia', description: 'Ao escolher uma data, o painel ao lado detalha vendas, valor de abertura, caixas e mesas abertas naquele dia.' },
  { area: 'HISTORY', target: 'reports-day-details', title: 'Resumo do dia selecionado', description: 'Os totais refletem apenas a data escolhida. Se ainda não houver movimento, o guia mostra uma prévia sem criar registros.' },
  { area: 'HISTORY', target: 'reports-shift-history', title: 'Inspecione um fechamento', description: 'Expanda um caixa para conferir abertura, vendas em dinheiro, suprimentos, sangrias, saldo esperado e mesas pendentes.' },
  { area: 'HISTORY', target: 'reports-correction', title: 'Corrigir saldo fechado', description: 'Uma correção exige novo saldo, motivo e senha autorizada. O relatório preserva o valor anterior e quem fez a mudança.' },
  { area: 'HISTORY', target: 'reports-pending', title: 'Receber mesa pendente', description: 'Quando uma mesa ficou aberta no fechamento, o pagamento posterior é registrado com forma de pagamento e autorização.' },
  { area: 'HISTORY', target: 'reports-transaction', title: 'Confira cada venda', description: 'Abra uma venda fechada para ver horário, valor, divisão por forma de pagamento e itens consumidos.' }
];

const toDateKey = (value: string | Date) => {
  const date = new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateTitle = (dateKey: string) => {
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
};

const TransactionItem: React.FC<{ tx: Transaction }> = ({ tx }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const paymentBreakdown = useMemo(() => {
      return tx.payments?.reduce((acc, p) => {
          const key = p.method.replace('Cartão ', '');
          acc[key] = (acc[key] || 0) + p.amount;
          return acc;
      }, {} as Record<string, number>) || {};
  }, [tx.payments]);

  // FIX: Prisma 'Json' type can return a plain object instead of an array if there's only one item. Ensure it's always an array.
  const itemsArray: OrderItem[] = Array.isArray(tx.items) ? tx.items : (tx.items ? [tx.items as any] : []);

  return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <button onClick={() => setIsExpanded(!isExpanded)} className="w-full p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 text-left hover:bg-gray-50/50 transition-colors focus:outline-none">
            <div className="flex-1 w-full">
                <div className="flex items-center gap-3 mb-3">
                    <span className="text-sm font-black text-gray-800 tracking-tight block">{tx.tableLabel}</span>
                    <span className="text-xs font-mono text-gray-400">{new Date(tx.closedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-2 text-center border-t border-gray-100 pt-3">
                    <div className="min-w-[50px] max-w-full overflow-hidden"><p className="text-xs font-bold text-gray-800 truncate tabular-nums">{formatCurrency(paymentBreakdown['Dinheiro'] || 0)}</p><p className="text-[9px] font-semibold text-gray-400 uppercase">Dinheiro</p></div>
                    <div className="min-w-[50px] max-w-full overflow-hidden"><p className="text-xs font-bold text-gray-800 truncate tabular-nums">{formatCurrency(paymentBreakdown['Pix'] || 0)}</p><p className="text-[9px] font-semibold text-gray-400 uppercase">Pix</p></div>
                    <div className="min-w-[50px] max-w-full overflow-hidden"><p className="text-xs font-bold text-gray-800 truncate tabular-nums">{formatCurrency(paymentBreakdown['Débito'] || 0)}</p><p className="text-[9px] font-semibold text-gray-400 uppercase">Débito</p></div>
                    <div className="min-w-[50px] max-w-full overflow-hidden"><p className="text-xs font-bold text-gray-800 truncate tabular-nums">{formatCurrency(paymentBreakdown['Crédito'] || 0)}</p><p className="text-[9px] font-semibold text-gray-400 uppercase">Crédito</p></div>
                </div>
            </div>
            <div className="flex items-center self-stretch sm:self-center gap-4">
                <div className="text-right w-full sm:w-auto max-w-full bg-gray-50 p-3 rounded-lg border border-gray-200 overflow-hidden">
                    <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest block">Total</span>
                    <span className="font-black text-lg text-gray-900 block truncate tabular-nums">{formatCurrency(tx.total)}</span>
                </div>
                <ChevronDown className={`w-5 h-5 text-gray-300 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`} />
            </div>
        </button>
        {isExpanded && (
            <div className="px-4 pb-4 bg-white animate-in fade-in duration-300">
              <div className="p-4 border-t border-gray-100 bg-gray-50/50 rounded-xl">
                <h4 className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Itens Consumidos</h4>
                <div className="space-y-1 text-sm max-h-48 overflow-y-auto pr-2 no-scrollbar">
                    {itemsArray.map((item, index) => (
                        <div key={item.id || index} className="flex justify-between items-center text-gray-700 py-1">
                            <span className="flex-1 pr-2">
                                <span className="font-bold">{item.quantity}x</span> {item.productName}
                            </span>
                            <span className="font-semibold w-24 max-w-[45%] text-right truncate tabular-nums">
                                {formatCurrency(item.price * item.quantity)}
                            </span>
                        </div>
                    ))}
                </div>
              </div>
            </div>
        )}
      </div>
  );
};

export const Reports = () => {
  const { user, tables, transactions, currentCompany, verifyManagerAuth, refreshData, updateShiftSettings, cashierShift, closeCashier, updateClosedCashierReport, getCashierHistory, addCashMovement, logManagerAction } = useStore();
  const navigate = useNavigate();
  const [isPageAuthenticated, setIsPageAuthenticated] = useState(false);
  const [pageAuthPassword, setPageAuthPassword] = useState('');
  const [isCloseAuthModalOpen, setIsCloseAuthModalOpen] = useState(false);
  const [authPassword, setAuthPassword] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [useShift, setUseShift] = useState(false);
  const [shiftStartHour, setShiftStartHour] = useState('08');
  const [shiftStartMinute, setShiftStartMinute] = useState('00');
  const [shiftHour, setShiftHour] = useState('06');
  const [shiftMinute, setShiftMinute] = useState('00');
  const [cashierHistory, setCashierHistory] = useState<CashierShift[]>([]);
  const [selectedHistory, setSelectedHistory] = useState<CashierShift | null>(null);
  const [correctionModal, setCorrectionModal] = useState<CashierShift | null>(null);
  const [correctedBalance, setCorrectedBalance] = useState('');
  const [correctionReason, setCorrectionReason] = useState('');
  const [historyMonth, setHistoryMonth] = useState(() => new Date());
  const [selectedHistoryDate, setSelectedHistoryDate] = useState(() => toDateKey(new Date()));

  const [movementModal, setMovementModal] = useState<{ type: 'DEPOSIT' | 'WITHDRAWAL' } | null>(null);
  const [movementAmount, setMovementAmount] = useState('');
  const [movementReason, setMovementReason] = useState('');
  const [pendingPaymentModal, setPendingPaymentModal] = useState<{ shift: CashierShift; table: any } | null>(null);
  const [pendingPaymentMethod, setPendingPaymentMethod] = useState<Payment['method']>('Dinheiro');
  const [financialEntries, setFinancialEntries] = useState<FinancialEntry[]>([]);
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const [expenseCategory, setExpenseCategory] = useState('Compras e reposição');
  const [expenseDescription, setExpenseDescription] = useState('');
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expenseDate, setExpenseDate] = useState(() => toDateKey(new Date()));
  const [expenseNotes, setExpenseNotes] = useState('');
  const [trendRange, setTrendRange] = useState<7 | 14 | 31>(31);
  const [hoveredTrendDayKey, setHoveredTrendDayKey] = useState<string | null>(null);
  const [selectedTrendDayKey, setSelectedTrendDayKey] = useState<string | null>(null);
  const [activeReportArea, setActiveReportArea] = useState<'DAILY' | 'FINANCIAL' | 'HISTORY'>('DAILY');
  const [isReportNavDocked, setIsReportNavDocked] = useState(false);
  const reportNavRef = useRef<HTMLElement>(null);
  const [tourOpen, setTourOpen] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const tourStorageKey = `flowtab-reports-tour-v1:${user?.id}`;
  const tourSnapshotRef = useRef<{ area: ReportArea; settingsOpen: boolean; expenseOpen: boolean } | null>(null);

  const startTour = useCallback(() => {
    tourSnapshotRef.current = { area: activeReportArea, settingsOpen: isSettingsModalOpen, expenseOpen: isExpenseModalOpen };
    setIsSettingsModalOpen(false);
    setIsExpenseModalOpen(false);
    setActiveReportArea('DAILY');
    setTourStep(0);
    setTourOpen(true);
  }, [activeReportArea, isSettingsModalOpen, isExpenseModalOpen]);

  const changeTourStep = (index: number) => {
    const step = reportTourSteps[index];
    setActiveReportArea(step.area);
    setIsSettingsModalOpen(step.modal === 'settings');
    setIsExpenseModalOpen(step.modal === 'expense');
    setTourStep(index);
  };

  const finishTour = () => {
    window.localStorage.setItem(tourStorageKey, 'seen');
    void api.auth.completeTutorial('reports');
    const nextRoute = user ? advanceFirstAccessTour(user, 'reports') : null;
    const snapshot = tourSnapshotRef.current;
    setIsSettingsModalOpen(snapshot?.settingsOpen ?? false);
    setIsExpenseModalOpen(snapshot?.expenseOpen ?? false);
    setActiveReportArea(snapshot?.area ?? 'DAILY');
    tourSnapshotRef.current = null;
    setTourOpen(false);
    setTourStep(0);
    if (nextRoute) navigate(nextRoute, { replace: true });
  };

  const allowedHistoryMonths = useMemo(() => {
    const now = new Date();
    return {
      current: new Date(now.getFullYear(), now.getMonth(), 1),
      previous: new Date(now.getFullYear(), now.getMonth() - 1, 1)
    };
  }, []);

  const fetchHistory = async () => {
    const history = await getCashierHistory();
    setCashierHistory(history);
  };

  const fetchFinancialEntries = async () => {
    if (!currentCompany?.id) return;
    const entries = await api.financialEntries.getByCompany(currentCompany.id, 31);
    setFinancialEntries(entries || []);
  };

  useEffect(() => {
    if (!isPageAuthenticated) return;
    refreshData();
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPageAuthenticated]);

  useEffect(() => {
    if (!isPageAuthenticated) return;
    fetchFinancialEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany?.id, isPageAuthenticated]);

  useEffect(() => {
    if (!isPageAuthenticated || !user?.id || !currentCompany?.id || tourOpen || isSettingsModalOpen || isExpenseModalOpen) return;
    if (getActiveTourArea(user.id) === 'reports') {
      startTour();
      return;
    }
    let active = true;
    const checkTour = async () => {
      const status = await api.auth.getTutorialStatus('reports');
      if (!active) return;
      const seenLocally = window.localStorage.getItem(tourStorageKey) === 'seen';
      if (seenLocally && status && !status.completed) void api.auth.completeTutorial('reports');
      if (seenLocally || status?.completed) {
        window.localStorage.setItem(tourStorageKey, 'seen');
        return;
      }
      startTour();
    };
    void checkTour();
    return () => { active = false; };
  }, [isPageAuthenticated, user?.id, currentCompany?.id, tourOpen, isSettingsModalOpen, isExpenseModalOpen, tourStorageKey, startTour]);

  const handleReportAccessAuth = async (event: React.FormEvent) => {
    event.preventDefault();
    if (await verifyManagerAuth(pageAuthPassword)) {
      setIsPageAuthenticated(true);
      setPageAuthPassword('');
      return;
    }
    alert('Senha incorreta. Acesso negado.');
    setPageAuthPassword('');
  };

  useEffect(() => {
    const scrollContainer = reportNavRef.current?.closest('main');
    const updateReportNavPosition = () => {
      const navigationTop = reportNavRef.current?.getBoundingClientRect().top ?? Number.POSITIVE_INFINITY;
      const containerTop = scrollContainer?.getBoundingClientRect().top ?? 0;
      setIsReportNavDocked(navigationTop <= containerTop + 1);
    };
    updateReportNavPosition();
    scrollContainer?.addEventListener('scroll', updateReportNavPosition, { passive: true });
    return () => scrollContainer?.removeEventListener('scroll', updateReportNavPosition);
  }, []);

  useEffect(() => {
    if (currentCompany) {
      setUseShift(currentCompany?.useShiftManagement ?? false);
      setShiftStartHour((currentCompany?.shiftStartHour ?? 8).toString().padStart(2, '0'));
      setShiftStartMinute((currentCompany?.shiftStartMinute ?? 0).toString().padStart(2, '0'));
      setShiftHour((currentCompany?.shiftEndHour ?? 6).toString().padStart(2, '0'));
      setShiftMinute((currentCompany?.shiftEndMinute ?? 0).toString().padStart(2, '0'));
    }
  }, [currentCompany, isSettingsModalOpen]);

  const useShiftManagement = currentCompany?.useShiftManagement ?? false;

  const operationalShifts = useMemo(() => {
    const shifts = [...cashierHistory];
    if (cashierShift?.status === 'OPEN') {
      shifts.push(cashierShift);
    }
    return shifts
      .filter(shift => shift.openedAt)
      .sort((a, b) => new Date(b.openedAt).getTime() - new Date(a.openedAt).getTime());
  }, [cashierHistory, cashierShift]);

  const getOperationalDateKey = useCallback((value: string | Date) => {
    const date = new Date(value);
    const timestamp = date.getTime();

    const matchingShift = operationalShifts.find(shift => {
      const openedAt = new Date(shift.openedAt).getTime();
      const closedAt = shift.closedAt ? new Date(shift.closedAt).getTime() : Number.POSITIVE_INFINITY;
      return timestamp >= openedAt && timestamp <= closedAt;
    });

    if (matchingShift) {
      return toDateKey(matchingShift.openedAt);
    }

    if (!useShiftManagement || !currentCompany) {
      return toDateKey(date);
    }

    const startHour = currentCompany.shiftStartHour ?? 8;
    const startMinute = currentCompany.shiftStartMinute ?? 0;
    const businessStart = new Date(date);
    businessStart.setHours(startHour, startMinute, 0, 0);

    if (date < businessStart) {
      businessStart.setDate(businessStart.getDate() - 1);
    }

    return toDateKey(businessStart);
  }, [currentCompany, operationalShifts, useShiftManagement]);

  const dailyReport = useMemo(() => {
    const now = new Date();
    let startOfBusinessDay: Date;

    if (useShiftManagement && currentCompany) {
      const startHour = currentCompany?.shiftStartHour ?? 8;
      const startMinute = currentCompany?.shiftStartMinute ?? 0;
      const endHour = currentCompany?.shiftEndHour ?? 6;

      const businessDayStartToday = new Date(now);
      businessDayStartToday.setHours(startHour, startMinute, 0, 0);

      const shiftCrossesMidnight = endHour < startHour;
      
      if (now < businessDayStartToday && shiftCrossesMidnight) {
        startOfBusinessDay = new Date(now);
        startOfBusinessDay.setDate(now.getDate() - 1);
        startOfBusinessDay.setHours(startHour, startMinute, 0, 0);
      } else {
        startOfBusinessDay = businessDayStartToday;
      }

    } else {
      startOfBusinessDay = new Date(now);
      startOfBusinessDay.setHours(0, 0, 0, 0);
    }
    
    const todaysTransactions = transactions.filter(tx => {
      const txDate = new Date(tx.closedAt);
      return txDate >= startOfBusinessDay && txDate <= now;
    });

    const totalRevenue = todaysTransactions.reduce((acc, tx) => acc + tx.total, 0);

    const paymentBreakdown = todaysTransactions
      .flatMap(tx => tx.payments || [])
      .reduce((acc, p) => {
        const key = p.method.replace('Cartão ', '');
        acc[key] = (acc[key] || 0) + p.amount;
        return acc;
      }, {} as Record<string, number>);

    return { totalRevenue, paymentBreakdown };

  }, [transactions, currentCompany, useShiftManagement]);

  const reportSummary = useMemo(() => {
    const transactionCount = transactions.length;
    const totalRevenue = transactions.reduce((sum, tx) => sum + tx.total, 0);
    const averageTicket = transactionCount > 0 ? totalRevenue / transactionCount : 0;

    const productMap = new Map<string, { name: string; quantity: number; revenue: number }>();
    transactions.forEach(tx => {
      const itemsArray: OrderItem[] = Array.isArray(tx.items) ? tx.items : (tx.items ? [tx.items as any] : []);
      itemsArray.forEach(item => {
        const current = productMap.get(item.productName) || { name: item.productName, quantity: 0, revenue: 0 };
        current.quantity += item.quantity;
        current.revenue += item.price * item.quantity;
        productMap.set(item.productName, current);
      });
    });

    const topProducts = Array.from(productMap.values())
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);

    return { transactionCount, totalRevenue, averageTicket, topProducts };
  }, [transactions]);

  const financialSummary = useMemo(() => {
    const totalExpenses = financialEntries.reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
    const operatingResult = reportSummary.totalRevenue - totalExpenses;
    const margin = reportSummary.totalRevenue > 0 ? (operatingResult / reportSummary.totalRevenue) * 100 : 0;
    const expensesByCategory = Array.from(financialEntries.reduce((map, entry) => {
      map.set(entry.category, (map.get(entry.category) || 0) + Number(entry.amount || 0));
      return map;
    }, new Map<string, number>()).entries())
      .map(([category, amount]) => ({ category, amount }))
      .sort((left, right) => right.amount - left.amount)
      .slice(0, 4);

    return { totalExpenses, operatingResult, margin, expensesByCategory };
  }, [financialEntries, reportSummary.totalRevenue]);

  const financialFlow = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Array.from({ length: trendRange }, (_, index) => {
      const date = new Date(today);
      date.setDate(today.getDate() - (trendRange - 1 - index));
      const key = toDateKey(date);
      const revenue = transactions.filter(tx => getOperationalDateKey(tx.closedAt) === key).reduce((sum, tx) => sum + tx.total, 0);
      const expenses = financialEntries.filter(entry => toDateKey(entry.occurredAt) === key).reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
      return {
        key,
        label: trendRange === 31
          ? date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }).replace('.', '')
          : date.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', ''),
        dateLabel: date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long' }),
        revenue,
        expenses,
        result: revenue - expenses,
      };
    });
  }, [financialEntries, getOperationalDateKey, transactions, trendRange]);

  const financialTrendChart = useMemo(() => {
    const values = financialFlow.flatMap(day => [day.revenue, day.expenses, day.result]);
    const minimum = Math.min(0, ...values);
    const maximum = Math.max(1, ...values);
    const left = 48;
    const top = 18;
    const width = 676;
    const height = 174;
    const scaleX = (index: number) => left + (financialFlow.length <= 1 ? width / 2 : (index / (financialFlow.length - 1)) * width);
    const scaleY = (value: number) => top + ((maximum - value) / (maximum - minimum || 1)) * height;
    const makePoints = (getValue: (day: typeof financialFlow[number]) => number, startIndex = 0, endIndex = financialFlow.length - 1) => financialFlow
      .slice(startIndex, endIndex + 1)
      .map((day, index) => `${scaleX(index + startIndex)},${scaleY(getValue(day))}`)
      .join(' ');
    const activityDays = financialFlow.filter(day => day.revenue > 0 || day.expenses > 0);
    const topRevenueDay = financialFlow.reduce((highest, day) => day.revenue > highest.revenue ? day : highest, financialFlow[0]);
    const lastRevenueIndex = financialFlow.reduce((lastIndex, day, index) => day.revenue > 0 ? index : lastIndex, -1);
    const lastExpenseIndex = financialFlow.reduce((lastIndex, day, index) => day.expenses > 0 ? index : lastIndex, -1);
    const hasExpenses = lastExpenseIndex >= 0;
    return {
      minimum,
      maximum,
      hasExpenses,
      activityDays,
      topRevenueDay,
      lastRevenueIndex,
      lastExpenseIndex,
      scaleX,
      scaleY,
      revenuePoints: makePoints(day => day.revenue, 0, Math.max(0, lastRevenueIndex)),
      revenueContinuationPoints: lastRevenueIndex >= 0 && lastRevenueIndex < financialFlow.length - 1 ? makePoints(day => day.revenue, lastRevenueIndex, financialFlow.length - 1) : '',
      expensePoints: hasExpenses ? makePoints(day => day.expenses, 0, Math.max(0, lastExpenseIndex)) : '',
      expenseContinuationPoints: hasExpenses && lastExpenseIndex < financialFlow.length - 1 ? makePoints(day => day.expenses, lastExpenseIndex, financialFlow.length - 1) : '',
    };
  }, [financialFlow]);

  const activeTrendDay = useMemo(() => {
    const activeKey = hoveredTrendDayKey || selectedTrendDayKey;
    const index = financialFlow.findIndex(day => day.key === activeKey);
    return index >= 0 ? { day: financialFlow[index], index } : null;
  }, [financialFlow, hoveredTrendDayKey, selectedTrendDayKey]);

  const paymentMix = useMemo(() => {
    const total = reportSummary.totalRevenue || 1;
    const totals = transactions.flatMap(transaction => transaction.payments || []).reduce((map, payment) => {
      const method = payment.method.replace('Cartão ', '');
      map.set(method, (map.get(method) || 0) + Number(payment.amount || 0));
      return map;
    }, new Map<string, number>());
    return ['Dinheiro', 'Pix', 'Débito', 'Crédito'].map(method => ({ method, amount: totals.get(method) || 0, percent: ((totals.get(method) || 0) / total) * 100 }));
  }, [reportSummary.totalRevenue, transactions]);

  const recentExpenses = useMemo(() => financialEntries.slice(0, 4), [financialEntries]);

  const dailyTopProducts = useMemo(() => {
    const todayKey = getOperationalDateKey(new Date());
    const products = new Map<string, { name: string; quantity: number; revenue: number }>();
    transactions.filter(transaction => getOperationalDateKey(transaction.closedAt) === todayKey).forEach(transaction => {
      const items = Array.isArray(transaction.items) ? transaction.items : (transaction.items ? [transaction.items as any] : []);
      items.forEach((item: OrderItem) => {
        const current = products.get(item.productName) || { name: item.productName, quantity: 0, revenue: 0 };
        current.quantity += Number(item.quantity || 0);
        current.revenue += Number(item.price || 0) * Number(item.quantity || 0);
        products.set(item.productName, current);
      });
    });
    return Array.from(products.values()).sort((left, right) => right.revenue - left.revenue).slice(0, 5);
  }, [getOperationalDateKey, transactions]);

  const lastSevenDaysSales = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const days = Array.from({ length: 7 }, (_, index) => {
      const date = new Date(today);
      date.setDate(today.getDate() - (6 - index));
      const key = toDateKey(date);
      const dayTransactions = transactions.filter(tx => getOperationalDateKey(tx.closedAt) === key);
      const total = dayTransactions.reduce((sum, tx) => sum + tx.total, 0);

      return {
        key,
        label: date.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', ''),
        day: date.getDate(),
        total,
        count: dayTransactions.length
      };
    });

    const maxTotal = Math.max(...days.map(day => day.total), 1);
    const totalPeriod = days.reduce((sum, day) => sum + day.total, 0);
    const bestDay = days.reduce((best, day) => day.total > best.total ? day : best, days[0]);

    return { days, maxTotal, totalPeriod, bestDay };
  }, [transactions, getOperationalDateKey]);

  const reportData = useMemo(() => {
    if (!cashierShift || cashierShift?.status !== 'OPEN') return null;

    const currentTurnTransactions = transactions.filter(t => new Date(t.closedAt).getTime() > new Date(cashierShift.openedAt).getTime());

    const openingFloat = cashierShift.openingFloat;
    const cashSales = currentTurnTransactions
        .flatMap(tx => tx.payments || [])
        .filter(p => p.method === 'Dinheiro')
        .reduce((sum, p) => sum + p.amount, 0);

    const movements = (Array.isArray(cashierShift.movements) ? cashierShift.movements : []) as CashMovement[];
    const deposits = movements.filter(m => m.type === 'DEPOSIT').reduce((sum, m) => sum + m.amount, 0);
    const withdrawals = movements.filter(m => m.type === 'WITHDRAWAL').reduce((sum, m) => sum + m.amount, 0);

    const expectedBalance = openingFloat + cashSales + deposits - withdrawals;

    return { openingFloat, cashSales, deposits, withdrawals, expectedBalance };
  }, [cashierShift, transactions]);
  
  const openTables = tables.filter(t => t.id !== COUNTER_TABLE_ID && t.status !== 'FREE');

  const historyCalendar = useMemo(() => {
    const monthStart = new Date(historyMonth.getFullYear(), historyMonth.getMonth(), 1);
    const monthEnd = new Date(historyMonth.getFullYear(), historyMonth.getMonth() + 1, 0);
    const calendarStart = new Date(monthStart);
    calendarStart.setDate(monthStart.getDate() - monthStart.getDay());

    const days = Array.from({ length: 42 }, (_, index) => {
      const day = new Date(calendarStart);
      day.setDate(calendarStart.getDate() + index);
      const key = toDateKey(day);
      const dayTransactions = transactions.filter(tx => getOperationalDateKey(tx.closedAt) === key);
      const dayShifts = cashierHistory.filter(shift => toDateKey(shift.openedAt) === key);
      const pendingTables = dayShifts
        .flatMap(shift => Array.isArray(shift.finalReport?.openTablesSnapshot) ? shift.finalReport.openTablesSnapshot : [])
        .filter((table: any) => !table.paidAt);
      return {
        key,
        date: day,
        isCurrentMonth: day >= monthStart && day <= monthEnd,
        transactions: dayTransactions,
        shifts: dayShifts,
        pendingTables,
        total: dayTransactions.reduce((sum, tx) => sum + tx.total, 0),
        openingTotal: dayShifts.reduce((sum, shift) => sum + Number(shift.openingFloat || 0), 0)
      };
    });

    return days;
  }, [historyMonth, transactions, cashierHistory, getOperationalDateKey]);

  const selectedHistoryDay = historyCalendar.find(day => day.key === selectedHistoryDate);

  const currentMonthIndex = allowedHistoryMonths.current.getFullYear() * 12 + allowedHistoryMonths.current.getMonth();
  const previousMonthIndex = allowedHistoryMonths.previous.getFullYear() * 12 + allowedHistoryMonths.previous.getMonth();
  const historyMonthIndex = historyMonth.getFullYear() * 12 + historyMonth.getMonth();
  const canGoToPreviousHistoryMonth = historyMonthIndex > previousMonthIndex;
  const canGoToNextHistoryMonth = historyMonthIndex < currentMonthIndex;

  const changeHistoryMonth = (direction: -1 | 1) => {
    const next = new Date(historyMonth.getFullYear(), historyMonth.getMonth() + direction, 1);
    const nextIndex = next.getFullYear() * 12 + next.getMonth();
    const targetMonth = nextIndex < previousMonthIndex
      ? allowedHistoryMonths.previous
      : nextIndex > currentMonthIndex
        ? allowedHistoryMonths.current
        : next;
    const targetIndex = targetMonth.getFullYear() * 12 + targetMonth.getMonth();
    setHistoryMonth(targetMonth);
    setSelectedHistoryDate(toDateKey(targetIndex === currentMonthIndex ? new Date() : targetMonth));
  };
  
  const handleCloseCashier = async (e: React.FormEvent) => {
    e.preventDefault();
    if (tourOpen) return;
    if (!reportData) return;
    setIsVerifying(true);
    const manager = await verifyManagerAuth(authPassword);
    if (manager) {
        window.print();
        const openTablesSnapshot = openTables.map(table => ({
          id: table.id,
          label: table.label,
          name: table.name || '',
          displayName: table.name ? `${table.label} - ${table.name}` : table.label,
          status: table.status,
          total: table.total,
          itemsCount: table.items?.length || 0,
          items: table.items || [],
          openedAt: table.openedAt || null
        }));
        const success = await closeCashier({
          ...reportData,
          managerName: manager.user.name,
          closedAt: new Date(),
          openTablesSnapshot,
          openTablesTotal: openTablesSnapshot.reduce((sum, table) => sum + Number(table.total || 0), 0)
        });
        if (success) {
            setIsCloseAuthModalOpen(false);
            setAuthPassword('');
            await fetchHistory();
        } else {
            alert("Erro ao fechar o caixa. Tente novamente.");
        }
    } else {
      alert("Senha de gerente incorreta.");
    }
    setIsVerifying(false);
  };

  const handleCashMovementSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (tourOpen) return;
    if (!movementModal) return;
    
    const amount = parseFloat(movementAmount.replace(',', '.'));
    if (isNaN(amount) || amount <= 0 || !movementReason) {
        alert('Valor ou motivo inválido.');
        return;
    }

    setIsVerifying(true);
    const authResult = await verifyManagerAuth(authPassword);
    
    if (authResult) {
      await addCashMovement({
          type: movementModal.type,
          amount,
          reason: movementReason,
          managerName: authResult.user.name
      });
      await logManagerAction(`${movementModal.type === 'DEPOSIT' ? 'Suprimento' : 'Sangria'}: ${formatCurrency(amount)} - ${movementReason}`, authResult.user.name);
      
      setMovementModal(null);
      setAuthPassword('');
      setMovementAmount('');
      setMovementReason('');
    } else {
      alert('Senha de gerente inválida.');
    }
    setIsVerifying(false);
  };

  const openPendingPayment = (shift: CashierShift, table: any) => {
    setPendingPaymentModal({ shift, table });
    setPendingPaymentMethod('Dinheiro');
    setAuthPassword('');
  };

  const handlePendingTablePayment = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!pendingPaymentModal || !currentCompany?.id) return;

    setIsVerifying(true);
    const manager = await verifyManagerAuth(authPassword);
    if (!manager) {
      alert('Senha de gerente incorreta.');
      setIsVerifying(false);
      return;
    }

    const table = pendingPaymentModal.table;
    const total = Number(table.total || 0);
    const transactionId = generateOrderId();
    const payment: Payment = {
      id: `pending-${Date.now()}`,
      method: pendingPaymentMethod,
      amount: total,
      timestamp: new Date()
    };
    const transaction: Transaction = {
      id: transactionId,
      companyId: currentCompany.id,
      tableLabel: `${table.displayName || table.label || `Mesa ${table.id}`} (paga depois)`,
      items: Array.isArray(table.items) ? table.items : [],
      payments: [payment],
      total,
      closedAt: new Date(),
      stockAlreadyReserved: true
    };

    const savedTransaction = await api.orders.save(transaction);
    if (!savedTransaction) {
      alert('Não foi possível lançar o pagamento dessa mesa agora.');
      setIsVerifying(false);
      return;
    }

    const shift = pendingPaymentModal.shift;
    const paidAt = new Date().toISOString();
    const updatedReport = {
      ...shift.finalReport,
      openTablesSnapshot: (shift.finalReport?.openTablesSnapshot || []).map((snapshot: any) =>
        String(snapshot.id) === String(table.id)
          ? {
              ...snapshot,
              paidAt,
              paidBy: manager.user.name,
              paymentMethod: pendingPaymentMethod,
              transactionId
            }
          : snapshot
      )
    };

    const updated = await updateClosedCashierReport(shift.id, updatedReport);
    if (!updated) {
      alert('O pagamento foi lançado, mas não consegui marcar a mesa como paga no histórico. Atualize e confira.');
      setIsVerifying(false);
      return;
    }

    await logManagerAction(`Pagamento posterior lançado: ${table.displayName || table.label} - ${formatCurrency(total)} em ${pendingPaymentMethod}`, manager.user.name);
    await refreshData();
    await fetchHistory();
    setSelectedHistory(prev => prev?.id === shift.id ? { ...shift, finalReport: updatedReport } : prev);
    setPendingPaymentModal(null);
    setAuthPassword('');
    setIsVerifying(false);
  };

  const openCorrectionModal = (shift: CashierShift) => {
    setCorrectionModal(shift);
    setCorrectedBalance(String(Number(shift.finalReport?.expectedBalance || 0)).replace('.', ','));
    setCorrectionReason('');
    setAuthPassword('');
  };

  const handleCorrectClosedCashier = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!correctionModal?.finalReport) return;

    const value = parseFloat(correctedBalance.replace(',', '.'));
    if (isNaN(value) || value < 0) {
      alert('Digite um saldo válido.');
      return;
    }

    if (!correctionReason.trim()) {
      alert('Informe o motivo da correção.');
      return;
    }

    setIsVerifying(true);
    const manager = await verifyManagerAuth(authPassword);
    if (!manager) {
      alert('Senha de gerente incorreta.');
      setIsVerifying(false);
      return;
    }

    const previousBalance = Number(correctionModal.finalReport?.expectedBalance || 0);
    const finalReport = {
      ...correctionModal.finalReport,
      expectedBalance: value,
      correctedAt: new Date(),
      correctedBy: manager.user.name,
      correctionReason: correctionReason.trim(),
      previousExpectedBalance: previousBalance
    };

    const success = await updateClosedCashierReport(correctionModal.id, finalReport);
    if (!success) {
      alert('Não foi possível corrigir esse caixa agora.');
      setIsVerifying(false);
      return;
    }

    await logManagerAction(`Correção de caixa fechado: ${formatCurrency(previousBalance)} para ${formatCurrency(value)} - ${correctionReason.trim()}`, manager.user.name);
    await fetchHistory();
    setSelectedHistory(prev => prev?.id === correctionModal.id ? { ...correctionModal, finalReport } : prev);
    setCorrectionModal(null);
    setAuthPassword('');
    setCorrectedBalance('');
    setCorrectionReason('');
    setIsVerifying(false);
  };
  
  const handleSaveShiftSettings = async () => {
    if (tourOpen) return;
    const hour = parseInt(shiftHour);
    const minute = parseInt(shiftMinute);
    const startHour = parseInt(shiftStartHour);
    const startMinute = parseInt(shiftStartMinute);
    
    if (!isNaN(hour) && !isNaN(minute) && !isNaN(startHour) && !isNaN(startMinute)) {
      await updateShiftSettings({ hour, minute, startHour, startMinute, useShift });
      setIsSettingsModalOpen(false);
    }
  };

  const handleCloseCashierClick = () => {
    if (openTables.length > 0) {
      const pendingTotal = openTables.reduce((sum, table) => sum + Number(table.total || 0), 0);
      if (window.confirm(`Existem ${openTables.length} mesa(s) aberta(s), somando ${formatCurrency(pendingTotal)}.\n\nElas serão registradas como pendentes no histórico do caixa. Deseja confirmar o fechamento mesmo assim?`)) {
        setIsCloseAuthModalOpen(true);
      }
    } else {
      setIsCloseAuthModalOpen(true);
    }
  };

  const goToReportArea = (area: 'DAILY' | 'FINANCIAL' | 'HISTORY') => {
    setActiveReportArea(area);
    requestAnimationFrame(() => {
      document.getElementById(area === 'DAILY' ? 'daily-dashboard' : area === 'FINANCIAL' ? 'financial-dashboard' : 'report-history')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const handleExpenseSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (tourOpen) return;
    if (!currentCompany?.id) return;
    const amount = Number(expenseAmount.replace(',', '.'));
    if (!expenseDescription.trim() || !Number.isFinite(amount) || amount <= 0) {
      alert('Informe uma descrição e um valor válido.');
      return;
    }
    setIsVerifying(true);
    const saved = await api.financialEntries.create({
      companyId: currentCompany.id,
      type: 'EXPENSE',
      category: expenseCategory,
      description: expenseDescription.trim(),
      amount,
      occurredAt: new Date(`${expenseDate}T12:00:00`),
      notes: expenseNotes.trim() || undefined
    });
    if (!saved) {
      alert('Não foi possível salvar o gasto agora.');
      setIsVerifying(false);
      return;
    }
    setFinancialEntries(previous => [saved, ...previous]);
    setIsExpenseModalOpen(false);
    setExpenseDescription('');
    setExpenseAmount('');
    setExpenseDate(toDateKey(new Date()));
    setExpenseNotes('');
    setIsVerifying(false);
  };

  const renderReportDetails = (report: any, shift?: CashierShift) => (
    <div className="space-y-4 text-sm">
        <div className="p-4 bg-gray-50 rounded-lg border space-y-2 overflow-hidden">
          <div className="flex justify-between items-center gap-3 text-gray-600 min-w-0"><span className="min-w-0 truncate">(+) Fundo de Troco (Abertura)</span><span className="font-bold text-green-600 shrink-0 tabular-nums">{formatCurrency(report.openingFloat)}</span></div>
          <div className="flex justify-between items-center gap-3 text-gray-600 min-w-0"><span className="min-w-0 truncate">(+) Total de Vendas em Dinheiro</span><span className="font-bold text-green-600 shrink-0 tabular-nums">{formatCurrency(report.cashSales)}</span></div>
          <div className="flex justify-between items-center gap-3 text-gray-600 min-w-0"><span className="min-w-0 truncate">(+) Suprimentos (Entradas Extra)</span><span className="font-bold text-green-600 shrink-0 tabular-nums">{formatCurrency(report.deposits)}</span></div>
          <div className="flex justify-between items-center gap-3 text-gray-600 min-w-0"><span className="min-w-0 truncate">(-) Sangrias (Retiradas)</span><span className="font-bold text-red-600 shrink-0 tabular-nums">{formatCurrency(report.withdrawals)}</span></div>
        </div>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 font-black text-lg sm:text-xl pt-3 text-brand-700 p-4 bg-brand-50 rounded-lg border-2 border-brand-100 overflow-hidden">
          <span className="min-w-0">Saldo esperado na gaveta</span>
          <span className="shrink-0 tabular-nums text-[clamp(1.1rem,3vw,1.5rem)]">{formatCurrency(report.expectedBalance)}</span>
        </div>
        {Array.isArray(report.openTablesSnapshot) && report.openTablesSnapshot.length > 0 && (
          <div className="p-4 bg-amber-50 rounded-lg border border-amber-100 overflow-hidden">
            <div className="flex justify-between items-center gap-3 mb-3 min-w-0">
              <span className="font-black text-amber-900 min-w-0 truncate">Mesas abertas no fechamento</span>
              <span className="font-black text-amber-700 shrink-0 tabular-nums">{formatCurrency(report.openTablesTotal || 0)}</span>
            </div>
            <div className="space-y-2">
              {report.openTablesSnapshot.map((table: any) => (
                <div key={`${table.id}-${table.label}`} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-xl bg-white/60 border border-amber-100 p-3 text-xs text-amber-900 min-w-0">
                  <div className="min-w-0">
                    <span className="font-black min-w-0 truncate block">{table.displayName || table.label} • {table.itemsCount} item(ns)</span>
                    {table.paidAt ? (
                      <span className="text-[10px] font-bold text-emerald-700">Pago depois em {new Date(table.paidAt).toLocaleString('pt-BR')} • {table.paymentMethod}</span>
                    ) : (
                      <span className="text-[10px] font-bold text-amber-700">Pendente para pagamento posterior</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between sm:justify-end gap-2 shrink-0">
                    <span className="font-black tabular-nums">{formatCurrency(table.total || 0)}</span>
                    {shift && !table.paidAt && (
                      <Button type="button" size="sm" onClick={() => openPendingPayment(shift, table)} className="h-8 rounded-xl bg-amber-500 hover:bg-amber-600 text-white">
                        Pagar depois
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
    </div>
  );

  if (!isPageAuthenticated) {
    return <ProtectedAreaGate title="Acesso aos relatórios" description={user && getActiveTourArea(user.id) === 'reports' ? 'Confirme a senha autorizada para continuar o tour completo. O guia de Relatórios abrirá automaticamente.' : 'Informe a senha do dono ou de um gerente autorizado para visualizar vendas, caixa, faturamento e financeiro.'} password={pageAuthPassword} onPasswordChange={setPageAuthPassword} onSubmit={handleReportAccessAuth} onBack={() => { if (user && getActiveTourArea(user.id) === 'reports') { stopFirstAccessTour(user.id); navigate('/pos'); } else navigate(-1); }} submitLabel="Acessar relatórios" accent="blue" />;
  }

  return (
    <div className="p-4 sm:p-8 bg-slate-50 min-h-full shrink-0 print:p-0 print:bg-white font-sans">
      <div id="printable-daily-report" className="hidden print:block text-black font-mono text-[10px] leading-tight p-4 w-[75mm]">
        <div className="text-center border-b border-black pb-3 mb-3">
          <h1 className="text-xs font-black uppercase tracking-widest">{currentCompany?.name}</h1>
          <p className="mt-1 font-bold">FECHAMENTO DE CAIXA</p>
          <p className="mt-0.5">Emitido em: {new Date().toLocaleString('pt-BR')}</p>
        </div>
        {reportData && (
          <div className="space-y-2 mb-3 pb-2 border-b border-dashed border-black">
            <h2 className="font-black text-xs">RESUMO DO CAIXA</h2>
            <div className="flex justify-between"><span>ABERTURA:</span><span className="font-bold">{formatCurrency(reportData.openingFloat)}</span></div>
            <div className="flex justify-between"><span>VENDAS (DINHEIRO):</span><span className="font-bold">{formatCurrency(reportData.cashSales)}</span></div>
            <div className="flex justify-between"><span>SUPRIMENTOS:</span><span className="font-bold">{formatCurrency(reportData.deposits)}</span></div>
            <div className="flex justify-between"><span>SANGRIAS:</span><span className="font-bold">-{formatCurrency(reportData.withdrawals)}</span></div>
            <div className="flex justify-between mt-2 font-black text-xs"><span>SALDO ESPERADO:</span><span>{formatCurrency(reportData.expectedBalance)}</span></div>
          </div>
        )}
        {openTables.length > 0 && (
          <div className="space-y-1 mb-3 pb-2 border-b border-dashed border-black">
            <h2 className="font-black text-xs">MESAS ABERTAS NO FECHAMENTO</h2>
            {openTables.map(table => (
              <div key={table.id} className="flex justify-between">
                <span>{table.label} ({table.items?.length || 0} item)</span>
                <span className="font-bold">{formatCurrency(table.total || 0)}</span>
              </div>
            ))}
          </div>
        )}
        <div className="mt-8 pt-4 border-t border-dashed border-black text-[8px]">
          <img
            src="/flowtab-logo-dark-transparent.png"
            alt="FlowTab"
            className="w-[36mm] mx-auto mb-1 opacity-85"
            style={{ transform: 'translateX(-4mm)' }}
          />
          <p className="text-center opacity-70">FlowTab - controle de comandas</p>
        </div>
      </div>
      <div className="max-w-6xl mx-auto flex flex-col gap-6 print:hidden animate-in fade-in duration-500">
        <header data-tour="reports-header" className="bg-gradient-to-br from-slate-950 via-slate-900 to-brand-900 rounded-[2rem] p-6 md:p-8 text-white shadow-xl shadow-slate-200 relative overflow-hidden">
          <div className="absolute -right-20 -top-20 w-64 h-64 bg-emerald-400/20 rounded-full blur-3xl"></div>
          <div className="relative flex flex-col lg:flex-row justify-between gap-6">
            <div>
              <button type="button" onClick={startTour} aria-label="Abrir guia dos Relatórios" className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-emerald-200 bg-white/10 border border-white/10 rounded-full px-3 py-1 mb-4 hover:bg-white/20">
                <HelpCircle className="w-4 h-4" /> Guia dos Relatórios
              </button>
              <h1 className="text-3xl md:text-4xl font-black tracking-tight">Relatórios & Fluxo de Caixa</h1>
              <p className="text-slate-300 text-sm md:text-base mt-2 max-w-2xl">Resumo financeiro, formas de pagamento, caixas fechados e vendas por dia em uma tela mais clara para tomada de decisão.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <button data-tour="reports-settings-button" onClick={() => setIsSettingsModalOpen(true)} className="h-12 px-4 rounded-2xl bg-white/10 hover:bg-white/15 border border-white/10 text-white text-xs font-black flex items-center justify-center gap-2 transition-colors">
                <Settings className="w-4 h-4" /> Config. Turno
              </button>
            </div>
          </div>
        </header>

        <nav ref={reportNavRef} data-tour="reports-nav" className={`sticky top-0 z-30 border border-slate-200 bg-white/95 p-1.5 shadow-lg shadow-slate-200/50 backdrop-blur ${isReportNavDocked ? 'rounded-t-none rounded-b-2xl border-t-0' : 'rounded-2xl'}`}>
          <div className="grid grid-cols-3 gap-1">
            {[['DAILY', 'Hoje'], ['FINANCIAL', 'Financeiro'], ['HISTORY', 'Histórico']].map(([area, label]) => <button key={area} onClick={() => goToReportArea(area as 'DAILY' | 'FINANCIAL' | 'HISTORY')} className={`h-10 rounded-xl text-xs font-black transition-all ${activeReportArea === area ? 'bg-slate-950 text-white shadow-sm' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'}`}>{label}</button>)}
          </div>
        </nav>

        <section id="daily-dashboard" data-tour="reports-daily-summary" className={`scroll-mt-44 grid md:grid-cols-2 xl:grid-cols-4 gap-4 ${activeReportArea === 'DAILY' ? '' : 'hidden'}`}>
          <div className="bg-white rounded-[1.5rem] border border-slate-200 shadow-sm p-5 min-w-0 overflow-hidden"><p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Faturamento hoje</p><p className="font-black text-emerald-600 mt-2 leading-tight tabular-nums truncate text-[clamp(1.35rem,2.6vw,1.875rem)]">{formatCurrency(dailyReport.totalRevenue)}</p><p className="text-xs text-slate-500 mt-2">Vendas desde o início do dia ou turno.</p></div>
          <div className="bg-white rounded-[1.5rem] border border-slate-200 shadow-sm p-5 min-w-0 overflow-hidden"><p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Vendas do período</p><p className="font-black text-slate-900 mt-2 leading-tight tabular-nums truncate text-[clamp(1.35rem,2.6vw,1.875rem)]">{reportSummary.transactionCount}</p><p className="text-xs text-slate-500 mt-2">Contas fechadas carregadas no relatório.</p></div>
          <div className="bg-white rounded-[1.5rem] border border-slate-200 shadow-sm p-5 min-w-0 overflow-hidden"><p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Ticket médio</p><p className="font-black text-brand-700 mt-2 leading-tight tabular-nums truncate text-[clamp(1.35rem,2.6vw,1.875rem)]">{formatCurrency(reportSummary.averageTicket)}</p><p className="text-xs text-slate-500 mt-2">Média por conta fechada.</p></div>
          <div className="bg-white rounded-[1.5rem] border border-slate-200 shadow-sm p-5 min-w-0 overflow-hidden"><p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Mesas abertas</p><p className="font-black text-amber-500 mt-2 leading-tight tabular-nums truncate text-[clamp(1.35rem,2.6vw,1.875rem)]">{openTables.length}</p><p className="text-xs text-slate-500 mt-2">Ainda não entram no faturamento.</p></div>
        </section>

        <section id="financial-dashboard" data-tour="reports-financial-summary" className={`scroll-mt-44 rounded-[2rem] border border-slate-200 bg-white shadow-sm overflow-hidden ${activeReportArea === 'FINANCIAL' ? '' : 'hidden'}`}>
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 p-5 sm:p-6 border-b border-slate-100">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.25em] text-emerald-600 flex items-center gap-2"><WalletCards className="w-4 h-4" /> Visão financeira</p>
              <h2 className="text-xl font-black text-slate-950 mt-1">Faturamento, gastos e resultado</h2>
              <p className="text-xs text-slate-500 mt-1">Últimos 31 dias carregados. Gastos são lançamentos reais da empresa; sangria não entra como despesa.</p>
            </div>
            <button data-tour="reports-expense-button" onClick={() => setIsExpenseModalOpen(true)} className="h-11 px-4 rounded-2xl bg-slate-950 hover:bg-slate-800 text-white text-xs font-black flex items-center justify-center gap-2 transition-colors">
              <Plus className="w-4 h-4" /> Lançar gasto
            </button>
          </div>
          <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-px bg-slate-100">
            <div className="bg-white p-5"><p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Faturamento</p><p className="mt-2 text-2xl font-black text-emerald-600 tabular-nums">{formatCurrency(reportSummary.totalRevenue)}</p><p className="mt-1 text-xs text-slate-500">Vendas fechadas no período.</p></div>
            <div className="bg-white p-5"><p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Gastos lançados</p><p className="mt-2 text-2xl font-black text-rose-600 tabular-nums">{formatCurrency(financialSummary.totalExpenses)}</p><p className="mt-1 text-xs text-slate-500">{financialEntries.length} lançamento(s) financeiro(s).</p></div>
            <div className="bg-white p-5"><p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Resultado operacional</p><p className={`mt-2 text-2xl font-black tabular-nums ${financialSummary.operatingResult >= 0 ? 'text-brand-700' : 'text-rose-600'}`}>{formatCurrency(financialSummary.operatingResult)}</p><p className="mt-1 text-xs text-slate-500">Faturamento menos gastos lançados.</p></div>
            <div className="bg-white p-5"><p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Margem atual</p><p className={`mt-2 text-2xl font-black tabular-nums ${financialSummary.margin >= 0 ? 'text-brand-700' : 'text-rose-600'}`}>{financialSummary.margin.toFixed(1).replace('.', ',')}%</p><p className="mt-1 text-xs text-slate-500">Resultado sobre o faturamento.</p></div>
          </div>
        </section>

        <section className={`grid xl:grid-cols-[1.35fr_0.65fr] gap-4 ${activeReportArea === 'FINANCIAL' ? '' : 'hidden'}`}>
          <div className="bg-white rounded-[2rem] border border-slate-200 shadow-sm p-5 sm:p-7 overflow-hidden">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
              <div><p className="text-[10px] font-black uppercase tracking-[0.25em] text-brand-600 flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Visão geral</p><h2 className="text-xl font-black text-slate-950 mt-1">Evolução financeira</h2><p className="text-xs text-slate-500 mt-1">Compare faturamento, gastos e saldo operacional no mesmo gráfico.</p></div>
              <div data-tour="reports-trend-range" className="inline-flex rounded-xl border border-slate-200 bg-slate-100 p-1 gap-1">{([7, 14, 31] as const).map(days => <button key={days} onClick={() => setTrendRange(days)} className={`h-8 px-3 rounded-lg text-[10px] font-black outline-none transition-all focus-visible:ring-2 focus-visible:ring-brand-400 ${trendRange === days ? 'bg-slate-950 text-white shadow-sm' : 'text-slate-500 hover:bg-white hover:text-slate-900'}`}>{days === 7 ? 'Semana' : days === 14 ? '14 dias' : 'Mensal'}</button>)}</div>
            </div>
            <div className="mt-5 flex flex-wrap items-center rounded-2xl border border-slate-200 bg-slate-50/80 px-1.5 py-1.5 text-xs">
              <div className="flex items-baseline gap-2 px-3 py-2"><span className="text-[9px] font-black uppercase tracking-wider text-slate-400">Maior entrada</span><span className="font-black text-emerald-700 tabular-nums">{formatCurrency(financialTrendChart.topRevenueDay?.revenue || 0)}</span></div>
              <div className="h-7 w-px bg-slate-200" />
              <div className="flex items-baseline gap-2 px-3 py-2"><span className="text-[9px] font-black uppercase tracking-wider text-slate-400">Movimento</span><span className="font-black text-slate-800">{financialTrendChart.activityDays.length} <span className="font-bold text-slate-400">dias</span></span></div>
              {financialTrendChart.topRevenueDay?.revenue > 0 && <><div className="hidden sm:block h-7 w-px bg-slate-200" /><div className="w-full px-3 pb-2 pt-1 text-[11px] font-bold text-slate-500 sm:w-auto sm:py-2"><span className="text-slate-400">Pico:</span> {financialTrendChart.topRevenueDay.dateLabel}</div></>}
            </div>
            <div data-tour="reports-trend-chart" className="mt-5 overflow-x-auto no-scrollbar"><div className="min-w-[620px] overflow-hidden rounded-2xl border border-blue-100 bg-white"><svg viewBox="0 0 760 252" className="w-full h-auto" role="img" aria-label="Gráfico de faturamento e gastos" onMouseLeave={() => setHoveredTrendDayKey(null)}>
              <defs>
                <filter id="blueLineShadow" x="-20%" y="-20%" width="140%" height="150%"><feDropShadow dx="1" dy="4" stdDeviation="2.5" floodColor="#2563eb" floodOpacity="0.28" /></filter>
                <filter id="amberLineShadow" x="-20%" y="-20%" width="140%" height="150%"><feDropShadow dx="1" dy="4" stdDeviation="2.5" floodColor="#f59e0b" floodOpacity="0.3" /></filter>
              </defs>
              {[0, 1, 2, 3, 4].map(index => { const y = financialTrendChart.scaleY(financialTrendChart.maximum - ((financialTrendChart.maximum - financialTrendChart.minimum) * index / 4)); const value = financialTrendChart.maximum - ((financialTrendChart.maximum - financialTrendChart.minimum) * index / 4); return <g key={index}><line x1="48" x2="724" y1={y} y2={y} stroke="#dbeafe" strokeWidth="1" /><text x="8" y={y - 6} className="fill-slate-400 text-[10px] font-bold">{formatCurrency(value)}</text></g>; })}
              {financialFlow.map((day, index) => { const center = financialTrendChart.scaleX(index); const labelVisible = trendRange <= 14 || index % 5 === 0 || index === financialFlow.length - 1; return <g key={day.key}>{labelVisible && <line x1={center} x2={center} y1="18" y2="196" stroke="#dbeafe" strokeWidth="1" />}{labelVisible && <text x={center} y="226" textAnchor="middle" className="fill-slate-500 text-[10px] font-black">{day.label}</text>}<title>{`${day.dateLabel}: entradas ${formatCurrency(day.revenue)} • gastos ${formatCurrency(day.expenses)}`}</title></g>; })}
              {financialTrendChart.revenuePoints && <polyline points={financialTrendChart.revenuePoints} fill="none" stroke="#2563eb" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" filter="url(#blueLineShadow)" />}
              {financialTrendChart.revenueContinuationPoints && <polyline points={financialTrendChart.revenueContinuationPoints} fill="none" stroke="#2563eb" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="4 7" opacity="0.9" filter="url(#blueLineShadow)" />}
              {financialTrendChart.expensePoints && <polyline points={financialTrendChart.expensePoints} fill="none" stroke="#f59e0b" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" filter="url(#amberLineShadow)" />}
              {financialTrendChart.expenseContinuationPoints && <polyline points={financialTrendChart.expenseContinuationPoints} fill="none" stroke="#f59e0b" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="4 7" opacity="0.9" filter="url(#amberLineShadow)" />}
              {financialFlow.map((day, index) => {
                const showRevenuePoint = day.revenue > 0;
                const showExpensePoint = day.expenses > 0;
                const isSelected = selectedTrendDayKey === day.key;
                const toggleSelectedDay = () => setSelectedTrendDayKey(currentKey => currentKey === day.key ? null : day.key);
                const pointHandlers = {
                  role: 'button' as const,
                  tabIndex: 0,
                  onMouseEnter: () => setHoveredTrendDayKey(day.key),
                  onFocus: () => setHoveredTrendDayKey(day.key),
                  onBlur: () => setHoveredTrendDayKey(null),
                  onClick: toggleSelectedDay,
                  onKeyDown: (event: React.KeyboardEvent<SVGCircleElement>) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      toggleSelectedDay();
                    }
                  },
                  className: 'cursor-pointer outline-none',
                  'aria-label': `${day.dateLabel}. Entradas ${formatCurrency(day.revenue)}, gastos ${formatCurrency(day.expenses)}, resultado ${formatCurrency(day.result)}.`,
                };
                return <g key={`point-${day.key}`}>{showRevenuePoint && <><circle cx={financialTrendChart.scaleX(index)} cy={financialTrendChart.scaleY(day.revenue)} r={isSelected ? "11" : "0"} fill="none" stroke="#2563eb" strokeWidth="2" opacity={isSelected ? "0.3" : "0"} /><circle {...pointHandlers} cx={financialTrendChart.scaleX(index)} cy={financialTrendChart.scaleY(day.revenue)} r={isSelected ? "7.5" : "6"} fill="#2563eb" stroke="white" strokeWidth={isSelected ? "3" : "2"} /></>}{showExpensePoint && <><circle cx={financialTrendChart.scaleX(index)} cy={financialTrendChart.scaleY(day.expenses)} r={isSelected ? "11" : "0"} fill="none" stroke="#f59e0b" strokeWidth="2" opacity={isSelected ? "0.35" : "0"} /><circle {...pointHandlers} cx={financialTrendChart.scaleX(index)} cy={financialTrendChart.scaleY(day.expenses)} r={isSelected ? "7.5" : "6"} fill="#f59e0b" stroke="white" strokeWidth={isSelected ? "3" : "2"} /></>}</g>;
              })}
              {activeTrendDay && (() => {
                const { day, index } = activeTrendDay;
                const pointY = financialTrendChart.scaleY(day.revenue || day.expenses);
                const tooltipWidth = 222;
                const tooltipHeight = 80;
                const tooltipX = Math.max(52, Math.min(720 - tooltipWidth, financialTrendChart.scaleX(index) - tooltipWidth / 2));
                const tooltipY = pointY < 104 ? pointY + 14 : pointY - tooltipHeight - 14;
                return <g pointerEvents="none"><rect x={tooltipX} y={tooltipY} width={tooltipWidth} height={tooltipHeight} rx="12" fill="#0f172a" filter="url(#blueLineShadow)" /><text x={tooltipX + 12} y={tooltipY + 17} className="fill-slate-300 text-[9px] font-bold">MOVIMENTO EM {day.dateLabel.toUpperCase()}</text><line x1={tooltipX + 111} x2={tooltipX + 111} y1={tooltipY + 26} y2={tooltipY + 58} stroke="#334155" /><text x={tooltipX + 12} y={tooltipY + 36} className="fill-slate-400 text-[9px] font-bold">ENTRADAS</text><text x={tooltipX + 12} y={tooltipY + 50} className="fill-white text-[11px] font-black">{formatCurrency(day.revenue)}</text><text x={tooltipX + 123} y={tooltipY + 36} className="fill-slate-400 text-[9px] font-bold">GASTOS</text><text x={tooltipX + 123} y={tooltipY + 50} className="fill-amber-300 text-[11px] font-black">{formatCurrency(day.expenses)}</text><text x={tooltipX + 12} y={tooltipY + 69} className="fill-emerald-300 text-[10px] font-black">Resultado {formatCurrency(day.result)}</text></g>;
              })()}
            </svg></div></div>
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-[10px] font-bold text-slate-500"><span className="inline-flex items-center gap-2"><i className="h-2.5 w-2.5 rounded-full bg-blue-600" /> Entradas</span><span className="inline-flex items-center gap-2"><i className="h-2.5 w-2.5 rounded-full bg-amber-500" /> Gastos</span><span className="text-slate-400">Passe o mouse na bolinha ou clique para ver o movimento.</span></div>
          </div>
          <div data-tour="reports-payment-mix" className="bg-white rounded-[2rem] border border-slate-200 shadow-sm p-5 sm:p-6">
            <div className="flex items-start justify-between gap-3 mb-5"><div><h2 className="font-black text-slate-900">Composição das vendas</h2><p className="text-xs text-slate-500 mt-1">Como o faturamento entrou.</p></div><Landmark className="w-7 h-7 text-brand-500" /></div>
            <div className="space-y-4">{paymentMix.map(payment => <div key={payment.method}><div className="flex justify-between gap-3 text-xs mb-1.5"><span className="font-bold text-slate-700">{payment.method}</span><span className="font-black text-slate-900 tabular-nums">{formatCurrency(payment.amount)}</span></div><div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-brand-500" style={{ width: `${payment.percent}%` }} /></div><p className="mt-1 text-[10px] text-slate-400">{payment.percent.toFixed(1).replace('.', ',')}% do faturamento</p></div>)}</div>
          </div>
        </section>

        <section className={`grid xl:grid-cols-2 gap-4 ${activeReportArea === 'FINANCIAL' ? '' : 'hidden'}`}>
          <div data-tour="reports-recent-expenses" className="bg-white rounded-[2rem] border border-slate-200 shadow-sm p-5 sm:p-6"><div className="flex items-start justify-between gap-3 mb-5"><div><h2 className="font-black text-slate-900">Gastos recentes</h2><p className="text-xs text-slate-500 mt-1">Últimos lançamentos registrados pela equipe.</p></div><button onClick={() => setIsExpenseModalOpen(true)} className="text-xs font-black text-rose-600 hover:text-rose-700">Adicionar</button></div>{recentExpenses.length === 0 ? <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-5 text-sm text-slate-500">Sem gastos lançados ainda. Ao cadastrar uma compra ou conta, ela aparece aqui e no resultado.</div> : <div className="divide-y divide-slate-100">{recentExpenses.map(entry => <div key={entry.id} className="py-3 flex items-center justify-between gap-4"><div className="min-w-0"><p className="font-bold text-sm text-slate-900 truncate">{entry.description}</p><p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mt-1">{entry.category} · {new Date(entry.occurredAt).toLocaleDateString('pt-BR')}</p></div><p className="font-black text-rose-600 tabular-nums shrink-0">-{formatCurrency(entry.amount)}</p></div>)}</div>}</div>
          <div data-tour="reports-expense-categories" className="bg-white rounded-[2rem] border border-slate-200 shadow-sm p-5 sm:p-6"><div className="flex items-start justify-between gap-3 mb-5"><div><h2 className="font-black text-slate-900">Onde o resultado aperta</h2><p className="text-xs text-slate-500 mt-1">Categorias que mais pesam no período.</p></div><ReceiptText className="w-7 h-7 text-rose-500" /></div>{financialSummary.expensesByCategory.length === 0 ? <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-5 text-sm text-slate-500">Ainda não há gastos lançados. Comece pelo custo de compras, aluguel ou contas do dia.</div> : <div className="space-y-4">{financialSummary.expensesByCategory.map((item, index) => <div key={item.category}><div className="flex items-center justify-between gap-3"><div className="flex items-center gap-3 min-w-0"><span className="w-7 h-7 rounded-xl bg-rose-50 text-rose-600 text-xs font-black flex items-center justify-center">{index + 1}</span><p className="font-bold text-sm text-slate-800 truncate">{item.category}</p></div><p className="font-black text-rose-600 text-sm tabular-nums">{formatCurrency(item.amount)}</p></div><div className="mt-2 ml-10 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-rose-400 rounded-full" style={{ width: `${financialSummary.totalExpenses ? (item.amount / financialSummary.totalExpenses) * 100 : 0}%` }} /></div></div>)}</div>}</div>
        </section>

        <section className={`grid lg:grid-cols-[1.15fr_0.85fr] gap-4 ${activeReportArea === 'DAILY' ? '' : 'hidden'}`}>
          <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-slate-200">
            <div data-tour="reports-daily-payments" className="flex items-center justify-between gap-4 mb-5">
              <div>
                <h2 className="font-black text-slate-900">Formas de pagamento de hoje</h2>
                <p className="text-xs text-slate-500 mt-1">Mostra como o dinheiro entrou: caixa físico, Pix e cartões.</p>
              </div>
              <Coins className="w-8 h-8 text-emerald-500" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4 text-center"><p className="text-sm font-black text-emerald-800">{formatCurrency(dailyReport.paymentBreakdown['Dinheiro'] || 0)}</p><p className="text-[9px] font-black text-emerald-600 uppercase mt-1">Dinheiro</p></div>
              <div className="rounded-2xl bg-cyan-50 border border-cyan-100 p-4 text-center"><p className="text-sm font-black text-cyan-800">{formatCurrency(dailyReport.paymentBreakdown['Pix'] || 0)}</p><p className="text-[9px] font-black text-cyan-600 uppercase mt-1">Pix</p></div>
              <div className="rounded-2xl bg-indigo-50 border border-indigo-100 p-4 text-center"><p className="text-sm font-black text-indigo-800">{formatCurrency(dailyReport.paymentBreakdown['Débito'] || 0)}</p><p className="text-[9px] font-black text-indigo-600 uppercase mt-1">Débito</p></div>
              <div className="rounded-2xl bg-violet-50 border border-violet-100 p-4 text-center"><p className="text-sm font-black text-violet-800">{formatCurrency(dailyReport.paymentBreakdown['Crédito'] || 0)}</p><p className="text-[9px] font-black text-violet-600 uppercase mt-1">Crédito</p></div>
            </div>
          </div>

           <div data-tour="reports-top-products" className="bg-white p-6 rounded-[2rem] shadow-sm border border-slate-200">
             <h2 className="font-black text-slate-900">Produtos mais vendidos hoje</h2>
            <p className="text-xs text-slate-500 mt-1 mb-4">Ranking somente das vendas do dia atual.</p>
            {dailyTopProducts.length === 0 ? (
               tourOpen ? <div className="rounded-xl border border-dashed border-emerald-200 bg-emerald-50 p-3 text-sm font-bold text-emerald-800"><span className="text-[10px] uppercase tracking-widest">Exemplo do guia</span><p>1. Produto de exemplo · 3 un. · R$ 36,00</p></div> : <p className="text-sm text-slate-400 italic">Sem vendas hoje para ranquear ainda.</p>
            ) : (
              <div className="space-y-3">
                {dailyTopProducts.map((product, index) => (
                  <div key={product.name} className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-8 h-8 rounded-xl bg-slate-100 text-slate-700 font-black text-xs flex items-center justify-center">{index + 1}</span>
                      <div className="min-w-0"><p className="font-black text-sm text-slate-900 truncate">{product.name}</p><p className="text-[10px] text-slate-400 font-bold">{product.quantity} un.</p></div>
                    </div>
                    <p className="font-black text-sm text-emerald-600">{formatCurrency(product.revenue)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
        
        {activeReportArea === 'DAILY' && useShiftManagement && (
            <section data-tour={cashierShift?.status === 'OPEN' ? 'reports-shift' : undefined} className="bg-white p-6 sm:p-8 rounded-[2rem] sm:rounded-[3rem] shadow-lg shadow-gray-200/50 border border-gray-100">
                <div className="flex flex-col md:flex-row items-center gap-6 sm:gap-8">
                  <div className="flex-1 w-full relative z-10 text-center md:text-left">
                    <p className="text-[10px] text-brand-600 font-black uppercase tracking-widest mb-2 flex items-center gap-2 justify-center md:justify-start">
                        <Coins className="w-4 h-4" /> Gestão do Turno Atual
                    </p>
                    <p className="text-lg font-bold text-gray-500">
                        {cashierShift?.status === 'OPEN' ? `Caixa aberto às ${new Date(cashierShift.openedAt).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}.` : 'Nenhum caixa aberto.'}
                    </p>
                  </div>
                  <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto relative z-10">
                      {cashierShift?.status === 'OPEN' && (
                        <>
                          <div className="flex gap-3 flex-1">
                              <Button data-tour="reports-deposit" onClick={() => setMovementModal({ type: 'DEPOSIT' })} disabled={tourOpen} variant="secondary" className="h-16 rounded-xl flex-col text-[10px] leading-tight pt-1 flex-1 !text-green-600">
                                  <ArrowUp className="w-5 h-5 mb-1"/> Suprimento
                              </Button>
                              <Button data-tour="reports-withdrawal" onClick={() => setMovementModal({ type: 'WITHDRAWAL' })} disabled={tourOpen} variant="secondary" className="h-16 rounded-xl flex-col text-[10px] leading-tight pt-1 flex-1 !text-red-600">
                                  <ArrowDown className="w-5 h-5 mb-1"/> Sangria
                              </Button>
                          </div>
                          <button data-tour="reports-close" onClick={handleCloseCashierClick} disabled={tourOpen} className="h-16 sm:w-56 bg-red-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-2xl shadow-red-200 flex items-center justify-center gap-3 transition-all hover:bg-red-700 active:scale-95 border-b-4 border-red-800">
                              <Archive className="w-5 h-5" /> FECHAR CAIXA
                          </button>
                        </>
                      )}
                  </div>
                </div>

                {openTables.length > 0 && (
                   <div data-tour="reports-open-tables" className="mt-6 rounded-3xl border border-amber-200 bg-amber-50 p-5">
                    <p className="text-[10px] font-black uppercase tracking-widest text-amber-600">Atenção no fechamento</p>
                    <p className="text-sm font-bold text-amber-950 mt-1">
                      Existem {openTables.length} mesa(s) aberta(s). Se fechar o caixa assim, elas ficam registradas como pendentes no histórico do dia.
                    </p>
                  </div>
                 )}
                 {tourOpen && openTables.length === 0 && cashierShift?.status === 'OPEN' && <div data-tour="reports-open-tables" className="mt-6 rounded-3xl border border-dashed border-amber-200 bg-amber-50 p-5 text-sm font-bold text-amber-900">Exemplo do guia: mesas abertas no fechamento aparecem como pendentes no Histórico.</div>}
             </section>
         )}

         {activeReportArea === 'DAILY' && tourOpen && (!useShiftManagement || cashierShift?.status !== 'OPEN') && (
           <section data-tour="reports-shift" className="rounded-[2rem] border border-dashed border-emerald-200 bg-white p-5 shadow-sm sm:p-6">
             <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Prévia do guia · sem movimentação real</p>
             <h2 className="mt-2 text-lg font-black text-slate-950">Gestão do turno e do caixa</h2>
             <p className="mt-1 text-xs text-slate-500">Estes controles aparecem quando a gestão de turnos está ativa e há um caixa aberto.</p>
             <div className="mt-4 flex flex-wrap gap-2">
               <button data-tour="reports-deposit" type="button" disabled className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs font-black text-emerald-800">Suprimento</button>
               <button data-tour="reports-withdrawal" type="button" disabled className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs font-black text-rose-700">Sangria</button>
               <button data-tour="reports-close" type="button" disabled className="rounded-xl bg-slate-950 px-4 py-3 text-xs font-black text-white">Fechar caixa</button>
             </div>
             <div data-tour="reports-open-tables" className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs font-bold text-amber-900">Exemplo: mesas ainda abertas no fechamento ficam pendentes no Histórico.</div>
           </section>
         )}

         <section className={`bg-white rounded-[2rem] border border-slate-200 shadow-sm p-5 sm:p-6 overflow-hidden ${activeReportArea === 'HISTORY' ? '' : 'hidden'}`}>
           <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-6">
            <div className="min-w-0">
              <p className="text-[10px] font-black uppercase tracking-[0.25em] text-brand-600 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" /> Vendas dos últimos 7 dias
              </p>
              <h2 className="text-xl sm:text-2xl font-black text-slate-950 mt-1">Ritmo de faturamento</h2>
              <p className="text-xs sm:text-sm text-slate-500 mt-1">Visão rápida do movimento recente usando o histórico já carregado.</p>
            </div>
            <div className="grid grid-cols-2 gap-3 min-w-0">
              <div className="rounded-2xl bg-emerald-50 border border-emerald-100 px-4 py-3 min-w-0 overflow-hidden">
                <p className="text-[9px] font-black uppercase tracking-widest text-emerald-600">Total 7 dias</p>
                <p className="font-black text-emerald-700 tabular-nums truncate text-[clamp(1rem,2.4vw,1.5rem)]">{formatCurrency(lastSevenDaysSales.totalPeriod)}</p>
              </div>
              <div className="rounded-2xl bg-slate-50 border border-slate-200 px-4 py-3 min-w-0 overflow-hidden">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-500">Melhor dia</p>
                <p className="font-black text-slate-950 tabular-nums truncate text-[clamp(1rem,2.4vw,1.5rem)]">{formatCurrency(lastSevenDaysSales.bestDay.total)}</p>
              </div>
            </div>
          </div>
           <div data-tour="reports-weekly" className="grid grid-cols-7 gap-2 sm:gap-3 items-end h-48 sm:h-56">
            {lastSevenDaysSales.days.map(day => {
              const height = Math.max(10, Math.round((day.total / lastSevenDaysSales.maxTotal) * 100));
              const isBest = day.key === lastSevenDaysSales.bestDay.key && day.total > 0;
              return (
                <div key={day.key} className="h-full min-w-0 flex flex-col justify-end gap-2">
                  <div className="flex-1 flex items-end rounded-2xl bg-slate-50 border border-slate-100 p-1.5 sm:p-2 overflow-hidden">
                    <div
                      title={`${day.label}, dia ${day.day}: ${formatCurrency(day.total)} em ${day.count} venda(s)`}
                      className={`relative w-full rounded-xl transition-all flex items-start justify-center px-1 pt-1.5 ${isBest ? 'bg-emerald-500 shadow-lg shadow-emerald-100' : day.total > 0 ? 'bg-brand-500' : 'bg-slate-200'}`}
                      style={{ height: `${height}%` }}
                    >
                      {day.total > 0 && (
                        <span className="max-w-full truncate rounded-lg bg-white/90 px-1.5 py-0.5 text-[8px] sm:text-[9px] font-black tabular-nums text-slate-900 shadow-sm">
                          {formatCurrency(day.total)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-center min-w-0">
                    <p className="text-[10px] sm:text-xs font-black text-slate-700 capitalize truncate">{day.label}</p>
                    <p className="text-[9px] text-slate-400 font-bold tabular-nums">{day.day}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section id="report-history" className={`scroll-mt-44 grid xl:grid-cols-[0.95fr_1.05fr] gap-5 items-start ${activeReportArea === 'HISTORY' ? '' : 'hidden'}`}>
           <div className="bg-white rounded-[2rem] border border-slate-200 shadow-sm p-5 sm:p-6 min-h-0 xl:max-h-[calc(100vh-7rem)] xl:overflow-y-auto xl:overscroll-contain">
             <div data-tour="reports-calendar" className="flex items-center justify-between gap-3 mb-5">
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-[10px] font-black uppercase tracking-[0.25em] text-brand-600">Calendário financeiro</p>
                  <span
                    title="A agenda mantém acesso rápido ao mês atual e ao mês anterior. Períodos mais antigos ficam no relatório mensal enviado por e-mail e podem ser removidos dos servidores."
                    className="group relative inline-flex w-6 h-6 items-center justify-center rounded-xl bg-brand-50 text-brand-700 border border-brand-100 cursor-help"
                  >
                    <HelpCircle className="w-3.5 h-3.5" />
                    <span className="pointer-events-none absolute left-1/2 top-8 z-20 hidden w-72 -translate-x-1/2 rounded-2xl bg-slate-950 p-3 text-left text-[11px] font-bold leading-relaxed text-white shadow-2xl group-hover:block">
                      A agenda mantém acesso rápido ao mês atual e ao mês anterior. Períodos mais antigos ficam no relatório mensal enviado por e-mail e podem ser removidos dos servidores.
                    </span>
                  </span>
                </div>
                <h2 className="text-xl font-black text-slate-950 capitalize mt-1">
                  {historyMonth.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
                </h2>
              </div>
              <div className="flex gap-2">
                <button disabled={!canGoToPreviousHistoryMonth} onClick={() => changeHistoryMonth(-1)} className="w-10 h-10 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center transition-colors disabled:opacity-35 disabled:hover:bg-slate-100"><ChevronLeft className="w-5 h-5" /></button>
                <button disabled={!canGoToNextHistoryMonth} onClick={() => changeHistoryMonth(1)} className="w-10 h-10 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center transition-colors disabled:opacity-35 disabled:hover:bg-slate-100"><ChevronRight className="w-5 h-5" /></button>
              </div>
            </div>

            <div className="grid grid-cols-7 gap-2 text-center mb-2">
              {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(day => (
                <span key={day} className="text-[9px] font-black uppercase tracking-widest text-slate-400">{day}</span>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-2">
              {historyCalendar.map(day => {
                const hasActivity = day.transactions.length > 0 || day.shifts.length > 0 || day.pendingTables.length > 0;
                const isSelected = selectedHistoryDate === day.key;
                const tooltip = `${day.date.toLocaleDateString('pt-BR')}\nVendas: ${day.transactions.length}\nCaixas: ${day.shifts.length}\nMesas abertas: ${day.pendingTables.length}`;
                return (
                   <button
                     key={day.key}
                     data-tour={isSelected ? 'reports-calendar-day' : undefined}
                    title={tooltip}
                    onClick={() => setSelectedHistoryDate(day.key)}
                    className={`min-h-[70px] rounded-2xl border p-2 text-left transition-all overflow-hidden ${isSelected ? 'border-brand-500 bg-brand-50 shadow-lg shadow-brand-100 ring-1 ring-brand-200' : hasActivity ? 'border-slate-200 bg-white hover:border-brand-200 hover:bg-slate-50' : 'border-slate-100 bg-slate-50/70'} ${day.isCurrentMonth ? 'opacity-100' : 'opacity-35'}`}
                  >
                    <span className={`text-sm font-black ${isSelected ? 'text-brand-700' : 'text-slate-700'}`}>{day.date.getDate()}</span>
                    {hasActivity && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {day.transactions.length > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-black text-emerald-700"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>{day.transactions.length}</span>}
                        {day.shifts.length > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-slate-900 px-1.5 py-0.5 text-[9px] font-black text-white"><span className="w-1.5 h-1.5 rounded-full bg-white"></span>{day.shifts.length}</span>}
                        {day.pendingTables.length > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-black text-amber-700"><span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>{day.pendingTables.length}</span>}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

           <div className="bg-white rounded-[2rem] border border-slate-200 shadow-sm p-5 sm:p-6 min-h-0 xl:max-h-[calc(100vh-7rem)] flex flex-col overflow-hidden">
             <div data-tour="reports-day-details" className="flex items-start justify-between gap-4 mb-5">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-400">Dia selecionado</p>
                <h2 className="text-xl font-black text-slate-950 capitalize">{formatDateTitle(selectedHistoryDate)}</h2>
              </div>
              <CalendarDays className="w-8 h-8 text-brand-600" />
            </div>

             <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain pr-1">
               {tourOpen && <div className="mb-5 space-y-2 rounded-2xl border border-dashed border-emerald-200 bg-emerald-50/60 p-4 text-sm text-slate-700">
                 <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Exemplo do guia · dados fictícios</p>
                 <div data-tour="reports-shift-history" className="rounded-xl border border-slate-200 bg-white p-3"><p className="font-black text-slate-900">Caixa fechado de exemplo</p><p className="mt-1 text-xs">Abertura R$ 50,00 · vendas em dinheiro R$ 100,00 · saldo esperado R$ 150,00. Ao abrir um fechamento real, você vê os movimentos detalhados.</p></div>
                 <div data-tour="reports-correction" className="rounded-xl border border-slate-200 bg-white p-3"><p className="font-black text-slate-900">Corrigir saldo</p><p className="mt-1 text-xs">Novo saldo · motivo · senha autorizada. O valor anterior e o responsável ficam registrados.</p></div>
                 <div data-tour="reports-pending" className="rounded-xl border border-amber-200 bg-white p-3"><p className="font-black text-amber-900">Mesa pendente · Pagar depois</p><p className="mt-1 text-xs">Escolha Dinheiro, Pix, Débito ou Crédito e confirme com senha autorizada. Nenhuma mesa será cobrada no guia.</p></div>
                 <div data-tour="reports-transaction" className="rounded-xl border border-slate-200 bg-white p-3"><p className="font-black text-slate-900">Venda fechada de exemplo</p><p className="mt-1 text-xs">Total R$ 36,00 · Pix R$ 36,00 · Produto de exemplo, 3× R$ 12,00. Abra uma venda real para conferir os itens.</p></div>
               </div>}
              {!selectedHistoryDay || (selectedHistoryDay.transactions.length === 0 && selectedHistoryDay.shifts.length === 0 && selectedHistoryDay.pendingTables.length === 0) ? (
                <div className="text-center py-14 rounded-3xl border-2 border-dashed border-slate-200 bg-slate-50">
                  <Utensils className="w-12 h-12 mx-auto text-slate-300 mb-2"/>
                  <p className="text-slate-500 font-semibold">Nenhuma venda ou fechamento nesse dia.</p>
                </div>
              ) : (
                <div className="space-y-5">
                  <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
                    <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-3 sm:p-4 min-w-0 overflow-hidden"><p className="text-[9px] font-black uppercase text-emerald-600 truncate">Vendas</p><p className="font-black text-emerald-700 leading-tight tabular-nums whitespace-nowrap tracking-tight text-[clamp(0.82rem,1.15vw,1.2rem)]">{formatCurrency(selectedHistoryDay.total)}</p></div>
                    <div className="rounded-2xl bg-brand-50 border border-brand-100 p-3 sm:p-4 min-w-0 overflow-hidden"><p className="text-[9px] font-black uppercase text-brand-600 truncate">Abertura</p><p className="font-black text-brand-700 leading-tight tabular-nums whitespace-nowrap tracking-tight text-[clamp(0.82rem,1.15vw,1.2rem)]">{formatCurrency(selectedHistoryDay.openingTotal)}</p></div>
                    <div className="rounded-2xl bg-slate-50 border border-slate-200 p-3 sm:p-4 min-w-0 overflow-hidden"><p className="text-[9px] font-black uppercase text-slate-500 truncate">Caixas</p><p className="font-black text-slate-900 leading-tight tabular-nums whitespace-nowrap tracking-tight text-[clamp(1rem,1.4vw,1.45rem)]">{selectedHistoryDay.shifts.length}</p></div>
                    <div className="rounded-2xl bg-amber-50 border border-amber-100 p-3 sm:p-4 min-w-0 overflow-hidden"><p className="text-[9px] font-black uppercase text-amber-600 truncate">Mesas abertas</p><p className="font-black text-amber-700 leading-tight tabular-nums whitespace-nowrap tracking-tight text-[clamp(1rem,1.4vw,1.45rem)]">{selectedHistoryDay.pendingTables.length}</p></div>
                  </div>

                {selectedHistoryDay.shifts.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2"><Archive className="w-4 h-4" /> Fechamentos de caixa</h3>
                    {selectedHistoryDay.shifts.map(shift => (
                      <div key={shift.id} className="rounded-3xl border border-slate-200 bg-white overflow-hidden">
                        <button onClick={() => setSelectedHistory(selectedHistory?.id === shift.id ? null : shift)} className="w-full p-4 flex items-center justify-between gap-3 hover:bg-slate-50 transition-colors overflow-hidden">
                          <div className="flex items-center gap-3 text-left min-w-0">
                            <div className="w-11 h-11 rounded-2xl bg-slate-100 text-slate-500 flex items-center justify-center"><FileText className="w-5 h-5"/></div>
                            <div className="min-w-0">
                              <p className="font-black text-sm text-slate-900 truncate">Caixa fechado</p>
                              <p className="text-[10px] text-slate-500 font-bold truncate">
                                Aberto {new Date(shift.openedAt).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})} • Fechado {shift.closedAt ? new Date(shift.closedAt).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'}) : '--:--'}
                              </p>
                            </div>
                          </div>
                          <div className="text-right flex items-center gap-3 shrink-0 min-w-0">
                            <div className="hidden sm:block min-w-0 max-w-28 overflow-hidden">
                              <p className="text-[9px] font-black text-slate-400 uppercase">Abertura</p>
                              <p className="font-black text-brand-700 truncate tabular-nums">{formatCurrency(shift.openingFloat || 0)}</p>
                            </div>
                            <div className="min-w-0 max-w-32 overflow-hidden">
                              <p className="text-[9px] font-black text-slate-400 uppercase">Saldo final</p>
                              <p className="font-black text-slate-950 truncate tabular-nums">{formatCurrency(shift.finalReport?.expectedBalance || 0)}</p>
                            </div>
                            <ChevronDown className={`w-5 h-5 text-slate-300 transition-transform ${selectedHistory?.id === shift.id ? 'rotate-180' : ''}`} />
                          </div>
                        </button>
                        {selectedHistory?.id === shift.id && (
                          <div className="p-5 border-t border-slate-100 bg-slate-50/60 space-y-4">
                            {renderReportDetails(shift.finalReport, shift)}
                            {shift.finalReport?.correctedAt && (
                              <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 text-xs text-amber-900">
                                <p className="font-black">Caixa corrigido</p>
                                <p>Antes: {formatCurrency(shift.finalReport.previousExpectedBalance || 0)} • Corrigido por {shift.finalReport.correctedBy}</p>
                                <p>Motivo: {shift.finalReport.correctionReason}</p>
                              </div>
                            )}
                            <Button onClick={() => openCorrectionModal(shift)} variant="secondary" size="sm" className="rounded-xl">
                              <Save className="w-4 h-4" /> Corrigir saldo
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {selectedHistoryDay.transactions.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Vendas fechadas</h3>
                    {selectedHistoryDay.transactions.map(tx => <TransactionItem key={tx.id} tx={tx} />)}
                  </div>
                )}
                </div>
              )}
            </div>
          </div>
        </section>

      </div>

      {isSettingsModalOpen && (
        <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-md flex items-center justify-center p-4">
            <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-lg p-6 sm:p-8 animate-in zoom-in-95 duration-300">
                <div className="flex justify-between items-start mb-6">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.25em] text-brand-600 mb-2">Rotina do caixa</p>
                        <h3 className="text-2xl font-black text-slate-950">Configuração de turno</h3>
                        <p className="text-sm text-slate-500 mt-1">Defina como o FlowTab separa vendas, fechamentos e relatórios por dia operacional.</p>
                    </div>
                    <button onClick={() => setIsSettingsModalOpen(false)} className="p-2 -mr-2 text-gray-400 hover:text-red-500"><X className="w-5 h-5"/></button>
                </div>

                 <div data-tour="reports-settings-toggle" className={`flex items-center justify-between p-4 rounded-3xl border mb-5 ${useShift ? 'bg-emerald-50 border-emerald-100' : 'bg-slate-50 border-slate-200'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${useShift ? 'bg-emerald-100 text-emerald-700' : 'bg-white text-slate-500'}`}>
                      <Clock className="w-5 h-5" />
                    </div>
                    <div>
                      <label htmlFor="use-shift" className="text-sm font-black text-slate-900">Usar turno operacional</label>
                      <p className="text-xs text-slate-500">Ideal para bares que fecham após meia-noite.</p>
                    </div>
                  </div>
                  <div className="relative inline-block w-10 mr-1 align-middle select-none transition duration-200 ease-in">
                      <input type="checkbox" name="use-shift" id="use-shift" checked={useShift} onChange={(e) => setUseShift(e.target.checked)} className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"/>
                      <label htmlFor="use-shift" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"></label>
                  </div>
                </div>

                 <div data-tour="reports-settings-hours" className="grid sm:grid-cols-2 gap-4">
                  <div className="bg-slate-50 p-4 rounded-3xl border border-slate-200">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Abertura padrão</label>
                    <div className="flex items-center justify-center gap-2 mt-3">
                      <select value={shiftStartHour} onChange={e => setShiftStartHour(e.target.value)} className="w-full p-3 bg-white border border-slate-200 rounded-2xl text-2xl font-black text-center appearance-none">{Array.from({length: 24}, (_, i) => i.toString().padStart(2, '0')).map(h => <option key={h} value={h}>{h}</option>)}</select>
                      <span className="text-2xl font-black text-slate-400">:</span>
                      <select value={shiftStartMinute} onChange={e => setShiftStartMinute(e.target.value)} className="w-full p-3 bg-white border border-slate-200 rounded-2xl text-2xl font-black text-center appearance-none">{['00', '15', '30', '45'].map(m => <option key={m} value={m}>{m}</option>)}</select>
                    </div>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-3xl border border-slate-200">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Fechamento padrão</label>
                    <div className="flex items-center justify-center gap-2 mt-3">
                      <select value={shiftHour} onChange={e => setShiftHour(e.target.value)} className="w-full p-3 bg-white border border-slate-200 rounded-2xl text-2xl font-black text-center appearance-none">{Array.from({length: 24}, (_, i) => i.toString().padStart(2, '0')).map(h => <option key={h} value={h}>{h}</option>)}</select>
                      <span className="text-2xl font-black text-slate-400">:</span>
                      <select value={shiftMinute} onChange={e => setShiftMinute(e.target.value)} className="w-full p-3 bg-white border border-slate-200 rounded-2xl text-2xl font-black text-center appearance-none">{['00', '15', '30', '45'].map(m => <option key={m} value={m}>{m}</option>)}</select>
                    </div>
                  </div>
                </div>

                 <div data-tour="reports-settings-save" className="mt-4 rounded-3xl bg-slate-950 text-white p-5">
                  <p className="text-[10px] font-black uppercase tracking-widest text-emerald-300">Prévia do relatório</p>
                  <p className="font-black text-lg mt-1">{useShift ? `${shiftStartHour}:${shiftStartMinute} até ${shiftHour}:${shiftMinute}` : 'Dia calendário: 00:00 até 23:59'}</p>
                  <p className="text-xs text-slate-300 mt-2">Quando o turno cruza a madrugada, as vendas feitas antes do fechamento continuam no mesmo dia operacional.</p>
                </div>
                 <Button onClick={handleSaveShiftSettings} disabled={tourOpen} className="w-full mt-5 h-12 rounded-2xl"><Save className="w-4 h-4"/> Salvar configuração</Button>
            </div>
        </div>
      )}

      {isCloseAuthModalOpen && reportData && (
          <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-md flex items-center justify-center p-4">
              <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md p-8 animate-in zoom-in-95 duration-300 relative overflow-hidden">
                  <div className="flex justify-between items-start mb-4"><h3 className="text-lg font-bold text-gray-800">Fechar Caixa</h3><button onClick={() => setIsCloseAuthModalOpen(false)} className="p-2 -mr-2 text-gray-400 hover:text-red-500"><X className="w-5 h-5"/></button></div>
                  {renderReportDetails(reportData)}
                  {openTables.length > 0 && (
                    <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4">
                      <p className="text-[10px] font-black uppercase tracking-widest text-amber-600">Mesas abertas serão registradas</p>
                      <div className="mt-3 space-y-2">
                        {openTables.map(table => (
                          <div key={table.id} className="flex justify-between text-xs text-amber-950">
                            <span className="font-bold">{table.name ? `${table.label} - ${table.name}` : table.label} • {table.items?.length || 0} item(ns)</span>
                            <span className="font-black">{formatCurrency(table.total || 0)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <form onSubmit={handleCloseCashier} className="mt-6">
                      <label className="text-xs font-semibold text-gray-600">Senha do Gerente/Dono para Confirmar</label>
                      <input type="password" autoFocus className="w-full p-3 border-2 border-gray-200 rounded-xl mt-1 text-center font-bold text-lg" value={authPassword} onChange={(e) => setAuthPassword(e.target.value)} />
                      <Button type="submit" disabled={isVerifying} className="w-full h-12 mt-4"><CheckCircle className="w-4 h-4"/> {isVerifying ? 'Fechando...' : 'Confirmar e Imprimir'}</Button>
                  </form>
              </div>
          </div>
      )}

      {correctionModal && (
          <div className="fixed inset-0 z-[210] bg-black/60 backdrop-blur-md flex items-center justify-center p-4">
              <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md p-8 animate-in zoom-in-95 duration-300">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                        <h3 className="text-lg font-bold text-gray-800">Corrigir saldo do caixa</h3>
                        <p className="text-xs text-gray-500 mt-1">Use apenas para corrigir erro de digitação em caixa já fechado.</p>
                    </div>
                    <button onClick={() => setCorrectionModal(null)} className="p-2 -mr-2 text-gray-400 hover:text-red-500"><X className="w-5 h-5"/></button>
                  </div>
                  <div className="bg-red-50 border border-red-100 rounded-2xl p-4 mb-4">
                    <p className="text-[10px] font-black uppercase text-red-500">Saldo atual lançado</p>
                    <p className="text-2xl font-black text-red-700">{formatCurrency(correctionModal.finalReport?.expectedBalance || 0)}</p>
                  </div>
                  <form onSubmit={handleCorrectClosedCashier} className="space-y-4">
                      <input type="text" placeholder="Saldo correto (Ex: 563,00)" value={correctedBalance} onChange={e => setCorrectedBalance(e.target.value)} className="w-full p-3 border border-gray-300 rounded-xl mt-1 font-bold text-center" required />
                      <input type="text" placeholder="Motivo da correção" value={correctionReason} onChange={e => setCorrectionReason(e.target.value)} className="w-full p-3 border border-gray-300 rounded-xl mt-1" required />
                      <input type="password" placeholder="Senha do Gerente/Dono" autoFocus className="w-full p-3 border border-gray-300 rounded-xl mt-1" value={authPassword} onChange={(e) => setAuthPassword(e.target.value)} required/>
                      <Button type="submit" disabled={isVerifying} className="w-full h-12 rounded-xl">{isVerifying ? 'Corrigindo...' : 'Salvar correção'}</Button>
                  </form>
              </div>
          </div>
      )}

      {pendingPaymentModal && (
          <div className="fixed inset-0 z-[220] bg-black/60 backdrop-blur-md flex items-center justify-center p-4">
              <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md p-8 animate-in zoom-in-95 duration-300">
                  <div className="flex justify-between items-start mb-5">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.25em] text-amber-600 mb-2">Mesa pendente</p>
                        <h3 className="text-xl font-black text-gray-900">Lançar pagamento posterior</h3>
                        <p className="text-xs text-gray-500 mt-1">Registra a venda no histórico e marca essa pendência como paga.</p>
                    </div>
                    <button onClick={() => setPendingPaymentModal(null)} className="p-2 -mr-2 text-gray-400 hover:text-red-500"><X className="w-5 h-5"/></button>
                  </div>
                  <div className="rounded-3xl border border-amber-100 bg-amber-50 p-4 mb-4">
                    <p className="text-sm font-black text-amber-950">{pendingPaymentModal.table.displayName || pendingPaymentModal.table.label}</p>
                    <p className="text-[11px] font-bold text-amber-700 mt-1">{pendingPaymentModal.table.itemsCount || 0} item(ns) em aberto</p>
                    <p className="text-3xl font-black text-amber-800 mt-3 tabular-nums">{formatCurrency(pendingPaymentModal.table.total || 0)}</p>
                  </div>
                  <form onSubmit={handlePendingTablePayment} className="space-y-4">
                      <select value={pendingPaymentMethod} onChange={e => setPendingPaymentMethod(e.target.value as Payment['method'])} className="w-full p-3 border border-gray-300 rounded-xl font-bold bg-white">
                        <option value="Dinheiro">Dinheiro</option>
                        <option value="Pix">Pix</option>
                        <option value="Cartão Débito">Cartão Débito</option>
                        <option value="Cartão Crédito">Cartão Crédito</option>
                      </select>
                      <input type="password" placeholder="Senha do Gerente/Dono" autoFocus className="w-full p-3 border border-gray-300 rounded-xl mt-1" value={authPassword} onChange={(e) => setAuthPassword(e.target.value)} required/>
                      <Button type="submit" disabled={isVerifying} className="w-full h-12 rounded-xl bg-amber-500 hover:bg-amber-600 text-white">{isVerifying ? 'Lançando...' : 'Confirmar pagamento'}</Button>
                  </form>
              </div>
          </div>
      )}

      {isExpenseModalOpen && (
          <div className="fixed inset-0 z-[205] bg-black/60 backdrop-blur-md flex items-center justify-center p-4">
              <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-lg p-6 sm:p-8 animate-in zoom-in-95 duration-300">
                  <div className="flex justify-between items-start gap-4 mb-5">
                    <div><p className="text-[10px] font-black uppercase tracking-[0.2em] text-rose-500 mb-2">Financeiro</p><h3 className="text-xl font-black text-slate-900">Lançar gasto</h3><p className="text-xs text-slate-500 mt-1">Registre um gasto real. Ele entra no resultado e no fluxo de caixa.</p></div>
                    <button onClick={() => setIsExpenseModalOpen(false)} className="p-2 -mr-2 text-gray-400 hover:text-rose-500"><X className="w-5 h-5" /></button>
                  </div>
                  <form onSubmit={handleExpenseSubmit} className="space-y-4">
                    <div data-tour="reports-expense-basics" className="grid sm:grid-cols-2 gap-3">
                      <label className="text-xs font-bold text-slate-600">Categoria<select value={expenseCategory} onChange={event => setExpenseCategory(event.target.value)} className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white p-3 font-semibold text-slate-800"><option>Compras e reposição</option><option>Aluguel e estrutura</option><option>Funcionários</option><option>Contas e serviços</option><option>Marketing</option><option>Outros gastos</option></select></label>
                      <label className="text-xs font-bold text-slate-600">Data<input type="date" value={expenseDate} onChange={event => setExpenseDate(event.target.value)} className="mt-1.5 w-full rounded-xl border border-slate-200 p-3 font-semibold text-slate-800" required /></label>
                    </div>
                    <div data-tour="reports-expense-details" className="space-y-4">
                      <label className="block text-xs font-bold text-slate-600">Descrição<input value={expenseDescription} onChange={event => setExpenseDescription(event.target.value)} placeholder="Ex.: Compra de bebidas para reposição" className="mt-1.5 w-full rounded-xl border border-slate-200 p-3 font-semibold text-slate-800" required /></label>
                      <label className="block text-xs font-bold text-slate-600">Valor do gasto<input inputMode="decimal" value={expenseAmount} onChange={event => setExpenseAmount(event.target.value)} placeholder="Ex.: 250,00" className="mt-1.5 w-full rounded-xl border border-slate-200 p-3 font-black text-slate-900" required /></label>
                      <label className="block text-xs font-bold text-slate-600">Observação opcional<textarea value={expenseNotes} onChange={event => setExpenseNotes(event.target.value)} placeholder="Fornecedor, nota ou detalhe importante" className="mt-1.5 w-full min-h-20 rounded-xl border border-slate-200 p-3 text-slate-800 resize-none" /></label>
                    </div>
                    <Button data-tour="reports-expense-save" type="submit" disabled={isVerifying || tourOpen} className="w-full h-12 rounded-xl bg-rose-600 hover:bg-rose-700 text-white"><Save className="w-4 h-4" /> {isVerifying ? 'Salvando...' : 'Salvar gasto'}</Button>
                  </form>
              </div>
          </div>
      )}

      {movementModal && (
          <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-md flex items-center justify-center p-4">
              <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-sm p-8 animate-in zoom-in-95 duration-300">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                        <h3 className="text-lg font-bold text-gray-800">{movementModal.type === 'DEPOSIT' ? 'Suprimento de Caixa' : 'Sangria (Retirada)'}</h3>
                        <p className="text-xs text-gray-500">Registre uma movimentação no caixa atual.</p>
                    </div>
                    <button onClick={() => setMovementModal(null)} className="p-2 -mr-2 text-gray-400 hover:text-red-500"><X className="w-5 h-5"/></button>
                  </div>
                  <form onSubmit={handleCashMovementSubmit} className="space-y-4">
                      <input type="text" placeholder="Valor (Ex: 50,00)" value={movementAmount} onChange={e => setMovementAmount(e.target.value)} className="w-full p-3 border border-gray-300 rounded mt-1" required />
                      <input type="text" placeholder="Motivo (Ex: Troco inicial)" value={movementReason} onChange={e => setMovementReason(e.target.value)} className="w-full p-3 border border-gray-300 rounded mt-1" required />
                      <input type="password" placeholder="Senha do Gerente/Dono" autoFocus className="w-full p-3 border border-gray-300 rounded mt-1" value={authPassword} onChange={(e) => setAuthPassword(e.target.value)} required/>
                      <Button type="submit" disabled={isVerifying} className="w-full h-12">{isVerifying ? 'Salvando...' : 'Confirmar Movimentação'}</Button>
                  </form>
              </div>
          </div>
      )}

      {tourOpen && <SpotlightTour label="Guia dos Relatórios" steps={reportTourSteps} stepIndex={tourStep} onStepChange={changeTourStep} onFinish={finishTour} scrollBlock="center" />}

    </div>
  );
};
